import React, { useMemo, useRef, useState } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { skipToken } from "@reduxjs/toolkit/query";
import { useDispatch, useSelector } from "react-redux";
import {
  useGetPosOrderQuery,
  useAddPosOrderLineMutation,
  useUpdatePosOrderLineMutation,
  useUpdatePosOrderNotesMutation,
  useSubmitPosOrderMutation,
  useClosePosOrderMutation,
  useDeletePosOrderLineMutation,
  useProcessPosOrderPaymentMutation,
  useProcessPosInvoicePaymentMutation,
  useLazyGetPosOrderInvoicesQuery,
  useUpdatePosOrderCustomerMutation,
  usePosSeatHeartbeatMutation,
  useOpenPosDineInMutation,
  useOpenPosTakeawayMutation,
  useGetTaxRatesQuery,
  useGetUpsellCampaignsQuery,
  useGetPublicCatalogMenuQuery,
  useGetCatalogReferenceQuery,
} from "../../store/api";
import { api } from "../../store/api";
import DiscountForm from "../../components/pos/DiscountForm";
import UpsellSuggestionModal from "../../components/pos/UpsellSuggestionModal";
import ModifierSelector from "../../components/pos/ModifierSelector";
import ProcessPaymentModal from "../../components/pos/ProcessPaymentModal";
import CashSessionStartupModal from "../../components/pos/CashSessionStartupModal";
import PrintBillAction from "../../components/pos/PrintBillAction";
import Alert from "../../components/common/Alert";
import Button from "../../components/common/Button";
import Input from "../../components/common/Input";
import Textarea from "../../components/common/Textarea";
import Modal from "../../components/common/Modal";
import OnScreenKeyboard from "../../components/common/OnScreenKeyboard";
import HeldOrdersModal from "../../components/pos/HeldOrdersModal";
import {
  getPosErrorMessage,
  shouldNavigateToSeatBoard,
  POS_ERROR_CODES,
  isItemStationRequiredError,
  ITEM_STATION_REQUIRED_MESSAGE,
  isOrderingBlockedError,
} from "../../utils/posErrorHandler";
import {
  normalizeMoneyAmount,
  truncateMoneyAmount,
} from "../../utils/money";
import { useOrderRealtime } from "../../hooks/usePosRealtime";
import {
  useKeyboardShortcuts,
  POS_SHORTCUTS,
} from "../../hooks/useKeyboardShortcuts";
import useCartLeaveGuard from "../../hooks/useCartLeaveGuard";
import { useSubmitCashSessionGuard } from "../../hooks/useSubmitCashSessionGuard";
import { useTheme } from "../../context/ThemeContext";
import { generateUUID } from "../../utils/uuid";
import { isRetryablePaymentError } from "../../utils/paymentIdempotency";
import {
  sanitizeCustomerPhone,
  validateCustomerPhone,
  validateCustomerDetails,
} from "../../utils/customerValidation";
import {
  isItemEligibleForOrderType,
  normalizeOrderTypeForEligibility,
} from "../../utils/itemEligibility";
import { isChannelOrderingBlocked } from "../../utils/orderBlocking";
import {
  itemHasSelectableModifiers,
  resolveItemModifierGroups,
} from "../../utils/posModifiers";
import { useStationFeature } from "../../hooks/useStationFeature";
import { useStoreFeatures } from "../../hooks/useStoreFeatures";
import { useCustomerLookup } from "../../hooks/useCustomerLookup";
import { holdOrder } from "../../store/heldOrdersSlice";
import { getPosAdminDestination } from "../../utils/posAdminNavigation";
import {
  addItemToLocalDraft,
  buildCombinedDisplayLines,
  buildDraftStateFromOrder,
  buildSubmittedOrderSnapshot,
  changeGroupedDraftLineQty,
  clearLocalDraft,
  readLocalDraft,
  removeGroupedDraftLine,
  syncLocalDraftToServer,
  writeLocalDraft,
} from "../../utils/posLocalDraft";

const PAYMENT_CONFIRMATION_POLL_INTERVAL_MS = 2000;
const PAYMENT_CONFIRMATION_POLL_TIMEOUT_MS = 45000;

const toRateFraction = (value) => {
  const numeric = Number(value);
  if (!Number.isFinite(numeric) || numeric <= 0) return 0;
  return numeric > 1 ? numeric / 100 : numeric;
};

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

const isTerminalOrderStatus = (value) => {
  const normalized = String(value || "").trim().toUpperCase();
  return (
    normalized === "COMPLETED" ||
    normalized === "CANCELLED" ||
    normalized === "VOIDED" ||
    normalized === "RETURN_VOIDED"
  );
};

const isPaymentConfirmationRequiredError = (err) => {
  const title = err?.data?.title || err?.data?.code || err?.data?.errorCode;
  return (
    err?.status === 409 &&
    String(title || "").trim().toUpperCase() === "PAYMENT_CONFIRMATION_REQUIRED"
  );
};

const pollInvoiceUntilPaid = async ({
  orderId,
  invoiceId,
  fetchOrderInvoices,
  onWaiting,
}) => {
  const startedAt = Date.now();

  while (Date.now() - startedAt < PAYMENT_CONFIRMATION_POLL_TIMEOUT_MS) {
    const invoices = await fetchOrderInvoices(orderId).unwrap();
    const list = Array.isArray(invoices) ? invoices : [];
    const invoice = list.find((candidate) => candidate?.invoiceId === invoiceId) || null;
    const status = String(invoice?.status || "").toUpperCase();

    if (status === "PAID") {
      const grandTotal = Number(invoice?.grandTotal ?? 0);
      const orderLineIds = Array.isArray(invoice?.orderLineIds)
        ? invoice.orderLineIds.filter(Boolean)
        : [];
      const hasAllocations = orderLineIds.length > 0;
      const hasValue = Number.isFinite(grandTotal) && grandTotal > 0;

      if (hasValue && hasAllocations) {
        return { state: "PAID_OK", invoice };
      }
      return { state: "PAID_EMPTY", invoice };
    }

    if (onWaiting) onWaiting(invoice);
    await sleep(PAYMENT_CONFIRMATION_POLL_INTERVAL_MS);
  }

  return { state: "TIMEOUT", invoice: null };
};

const resolveReceiptInvoiceId = (order) => {
  const invoices = Array.isArray(order?.invoices) ? order.invoices : [];
  if (invoices.length === 0) return null;

  const sorted = [...invoices].sort((a, b) => {
    const aPaid = String(a?.status || "").toUpperCase() === "PAID";
    const bPaid = String(b?.status || "").toUpperCase() === "PAID";
    if (aPaid !== bPaid) return bPaid ? 1 : -1;

    const aNo = Number(a?.invoiceNo ?? 0);
    const bNo = Number(b?.invoiceNo ?? 0);
    if (Number.isFinite(aNo) && Number.isFinite(bNo) && aNo !== bNo) {
      return bNo - aNo;
    }

    const aTotal = Number(a?.grandTotal ?? 0);
    const bTotal = Number(b?.grandTotal ?? 0);
    if (Number.isFinite(aTotal) && Number.isFinite(bTotal) && aTotal !== bTotal) {
      return bTotal - aTotal;
    }

    return 0;
  });

  return sorted[0]?.invoiceId || null;
};

const resolvePayFirstInvoiceId = (order) => {
  const invoices = Array.isArray(order?.invoices) ? order.invoices : [];
  if (invoices.length === 0) return null;

  const sorted = [...invoices].sort((a, b) => {
    const aOpen = String(a?.status || "").toUpperCase() === "OPEN";
    const bOpen = String(b?.status || "").toUpperCase() === "OPEN";
    if (aOpen !== bOpen) return bOpen ? 1 : -1;

    const aNo = Number(a?.invoiceNo ?? 0);
    const bNo = Number(b?.invoiceNo ?? 0);
    if (Number.isFinite(aNo) && Number.isFinite(bNo) && aNo !== bNo) {
      return bNo - aNo;
    }

    return 0;
  });

  return sorted[0]?.invoiceId || null;
};

const getOrderPaidAmount = (orderSnapshot) => {
  if (!orderSnapshot) return 0;

  const invoicePaidTotal = Array.isArray(orderSnapshot?.invoices)
    ? orderSnapshot.invoices.reduce(
        (sum, invoice) => sum + Number(invoice?.paidAmount || 0),
        0,
      )
    : 0;

  const totalsPaid = Number(orderSnapshot?.totals?.paid);
  if (Number.isFinite(totalsPaid) && totalsPaid > 0) {
    return totalsPaid;
  }

  const paidAmount = Number(orderSnapshot?.paidAmount);
  if (Number.isFinite(paidAmount) && paidAmount > 0) {
    return paidAmount;
  }

  return invoicePaidTotal;
};

const getRemainingBalance = (orderSnapshot, fallbackGrandTotal = 0, fallbackPaid = 0) => {
  const grandTotal = Number(
    orderSnapshot?.totals?.grandTotal ?? fallbackGrandTotal ?? 0,
  );
  const paid = getOrderPaidAmount(orderSnapshot) || Number(fallbackPaid || 0);

  if (!Number.isFinite(grandTotal) || grandTotal <= 0) {
    return 0;
  }

  return Math.max(0, grandTotal - paid);
};

const getPendingCustomerStorageKey = (orderId) =>
  `pos.pendingCustomer.${orderId}`;

const readPendingCustomerFromStorage = (orderId) => {
  if (!orderId) return null;

  try {
    const raw = sessionStorage.getItem(getPendingCustomerStorageKey(orderId));
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

const toPositiveInt = (value) => {
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return 1;
  return Math.max(1, Math.trunc(parsed));
};

const getModifierSignature = (modifiers = []) => {
  if (!Array.isArray(modifiers) || modifiers.length === 0) return "";

  return modifiers
    .map((modifier) => {
      const optionId =
        modifier?.modifierId ||
        modifier?.optionId ||
        modifier?.modifierOptionId ||
        modifier?.productModifierOptionId ||
        modifier?.modifierItemId ||
        "";
      const fallbackName = String(
        modifier?.name || modifier?.modifierName || modifier?.optionName || "",
      )
        .trim()
        .toLowerCase();
      const fallbackPrice = Number(
        modifier?.priceDelta ?? modifier?.price ?? 0,
      ).toFixed(2);
      const identity =
        optionId || (fallbackName ? `${fallbackName}@${fallbackPrice}` : "");
      const qty = toPositiveInt(modifier?.qty);
      return identity ? `${identity}:${qty}` : "";
    })
    .filter(Boolean)
    .sort()
    .join("|");
};

const getModifiersTotalPrice = (modifiers = []) => {
  if (!Array.isArray(modifiers) || modifiers.length === 0) return 0;

  return modifiers.reduce((total, modifier) => {
    const priceDelta = Number(modifier?.priceDelta || modifier?.price || 0);
    const qty = toPositiveInt(modifier?.qty || 1);
    return total + priceDelta * qty;
  }, 0);
};

const getLineSubtotalValue = (line) =>
  Number(
    line?.lineSubtotal ??
      line?.lineSubTotal ??
      line?.subtotal ??
      line?.lineTotal ??
      0,
  );

const getEffectiveUnitPrice = (line) => {
  const basePrice = Number(line?.unitPrice || 0);
  const modifiersTotal = getModifiersTotalPrice(line?.modifiers);
  const qty = Math.max(0, Number(line?.qty || 0));
  const subtotal = getLineSubtotalValue(line);

  // Prefer backend-calculated unit price when available to avoid
  // double counting modifier prices across payload variants.
  if (qty > 0 && subtotal > 0) {
    const inferredUnitPrice = subtotal / qty;
    const expectedWithModifiers = basePrice + modifiersTotal;

    if (Math.abs(inferredUnitPrice - basePrice) < 0.01) {
      return basePrice;
    }

    if (Math.abs(inferredUnitPrice - expectedWithModifiers) < 0.01) {
      return expectedWithModifiers;
    }

    return inferredUnitPrice;
  }

  return basePrice + modifiersTotal;
};

const getModifierDisplayText = (modifiers = []) => {
  if (!Array.isArray(modifiers) || modifiers.length === 0) return "";

  return [...modifiers]
    .map((modifier) => ({
      id:
        modifier?.modifierId ||
        modifier?.optionId ||
        modifier?.modifierOptionId ||
        modifier?.productModifierOptionId ||
        modifier?.modifierItemId ||
        "",
      name:
        modifier?.name ||
        modifier?.modifierName ||
        modifier?.optionName ||
        "Modifier",
    }))
    .sort((a, b) => {
      const nameDiff = a.name.localeCompare(b.name);
      if (nameDiff !== 0) return nameDiff;
      return String(a.id).localeCompare(String(b.id));
    })
    .map((modifier) => modifier.name)
    .join(", ");
};

const splitModifiersIntoLineItems = (modifiers = []) => {
  const normalizedModifiers = Array.isArray(modifiers)
    ? modifiers.filter(Boolean)
    : [];

  if (normalizedModifiers.length <= 1) {
    return [normalizedModifiers];
  }

  return normalizedModifiers.map((modifier) => [modifier]);
};

const getLineGroupingKey = (line) => {
  const lineItemId = line?.itemId || line?.productId || "";
  const lineName = String(line?.itemName || line?.productName || "")
    .trim()
    .toLowerCase();
  const stationCode = String(line?.stationCode || "")
    .trim()
    .toUpperCase();
  const identity = lineItemId || `${lineName}::${stationCode}`;

  return `${identity}::${getModifierSignature(line?.modifiers)}`;
};

const getResolvedQtySentToKitchen = (line, getNumeric) => {
  const qty = Math.max(0, getNumeric(line?.qty));
  const reportedQtySent = Math.max(
    0,
    Math.min(qty, getNumeric(line?.qtySentToKitchen)),
  );

  if (reportedQtySent > 0) return reportedQtySent;
  if (line?.isSentToKitchen) return qty;
  return 0;
};

const groupDisplayLines = (lines = [], getNumeric, getLineDisplaySubtotal) => {
  const grouped = new Map();

  lines.forEach((line) => {
    const key = getLineGroupingKey(line);
    const existing = grouped.get(key);
    const resolvedQtySent = getResolvedQtySentToKitchen(line, getNumeric);

    if (existing) {
      existing.qty += getNumeric(line.qty);
      existing.lineSubtotal += getLineDisplaySubtotal(line);
      existing.qtySentToKitchen += resolvedQtySent;
      existing.isSentToKitchen =
        existing.isSentToKitchen || Boolean(line?.isSentToKitchen);
      existing.sourceLines.push(line);
      return;
    }

    grouped.set(key, {
      ...line,
      qty: getNumeric(line.qty),
      lineSubtotal: getLineDisplaySubtotal(line),
      qtySentToKitchen: resolvedQtySent,
      isSentToKitchen:
        Boolean(line?.isSentToKitchen) || resolvedQtySent > 0,
      sourceLines: [line],
    });
  });

  return Array.from(grouped.values());
};

const getKitchenStatus = (line, getNumeric) => {
  const qty = Math.max(0, getNumeric(line?.qty));
  const qtySent = getResolvedQtySentToKitchen(line, getNumeric);

  if (qtySent === 0) {
    return {
      code: "NOT_SENT",
      label: "Not sent",
      tone: "bg-orange-100 text-orange-700 dark:bg-orange-900/40 dark:text-orange-300",
      helper: "Not sent to kitchen yet",
      qtyPending: qty,
      qtySent,
    };
  }

  if (qtySent >= qty) {
    return {
      code: "SENT",
      label: "Sent",
      tone: "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300",
      helper: "Sent to kitchen",
      qtyPending: 0,
      qtySent,
    };
  }

  return {
    code: "PARTIALLY_SENT",
    label: "Partially sent",
    tone: "bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300",
    helper: `${qtySent} of ${qty} sent to kitchen`,
    qtyPending: qty - qtySent,
    qtySent,
  };
};

const getSplitLineSubtotal = (line, qtyPortion, getNumeric) => {
  const qty = Math.max(0, getNumeric(line?.qty));
  if (qty <= 0) return 0;
  const subtotal = getNumeric(
    line?.lineSubtotal ??
      line?.lineSubTotal ??
      line?.subtotal ??
      line?.lineTotal,
  );
  return (subtotal / qty) * qtyPortion;
};

const getSplitLineTotal = (line, qtyPortion, getNumeric) => {
  const qty = Math.max(0, getNumeric(line?.qty));
  if (qty <= 0) return 0;
  const total = getNumeric(
    line?.lineTotal ??
      line?.total ??
      line?.lineSubtotal ??
      line?.lineSubTotal ??
      line?.subtotal,
  );
  return (total / qty) * qtyPortion;
};

const normalizeLineModifiersForPayload = (modifiers = []) => {
  if (!Array.isArray(modifiers)) return [];

  return modifiers
    .map((modifier) => ({
      modifierId:
        modifier?.modifierId ||
        modifier?.optionId ||
        modifier?.modifierOptionId ||
        modifier?.productModifierOptionId ||
        modifier?.modifierItemId ||
        null,
      qty: toPositiveInt(modifier?.qty, 1),
    }))
    .filter((modifier) => Boolean(modifier.modifierId));
};

const getModifierOptionLookupKey = (name, price) => {
  const normalizedName = String(name || "")
    .trim()
    .toLowerCase();
  const normalizedPrice = Number(price ?? 0).toFixed(2);
  return `${normalizedName}::${normalizedPrice}`;
};

const resolvePerUnitModifierQty = (modifierQty, lineQty) => {
  const normalizedModifierQty = toPositiveInt(modifierQty, 1);
  const normalizedLineQty = Math.max(1, toPositiveInt(lineQty, 1));

  // Some backends return modifier qty aggregated at line level.
  // Convert back to per-unit qty when it is an exact multiple.
  if (
    normalizedLineQty > 1 &&
    normalizedModifierQty >= normalizedLineQty &&
    normalizedModifierQty % normalizedLineQty === 0
  ) {
    return Math.max(1, normalizedModifierQty / normalizedLineQty);
  }

  return normalizedModifierQty;
};

const resolveLineModifiersForPayload = (
  lineModifiers = [],
  modifierOptions = [],
  lineQty = 1,
) => {
  const normalized = normalizeLineModifiersForPayload(lineModifiers);
  if (normalized.length > 0) {
    return normalized.map((modifier) => ({
      ...modifier,
      qty: resolvePerUnitModifierQty(modifier.qty, lineQty),
    }));
  }

  if (!Array.isArray(lineModifiers) || lineModifiers.length === 0) return [];

  const optionByKey = new Map();
  (Array.isArray(modifierOptions) ? modifierOptions : []).forEach((option) => {
    const optionId =
      option?.id || option?.optionId || option?.modifierOptionId || null;
    if (!optionId) return;

    optionByKey.set(
      getModifierOptionLookupKey(
        option?.name || option?.displayName || "",
        option?.priceDelta ?? option?.price ?? 0,
      ),
      optionId,
    );
  });

  return lineModifiers
    .map((modifier) => {
      const resolvedId = optionByKey.get(
        getModifierOptionLookupKey(
          modifier?.name ||
            modifier?.modifierName ||
            modifier?.optionName ||
            "",
          modifier?.priceDelta ?? modifier?.price ?? 0,
        ),
      );
      return resolvedId
        ? {
            modifierId: resolvedId,
            qty: resolvePerUnitModifierQty(modifier?.qty, lineQty),
          }
        : null;
    })
    .filter(Boolean);
};

const buildLineQtyUpdatePayload = (line, qty) => {
  const payload = { qty };
  const modifiers = normalizeLineModifiersForPayload(line?.modifiers);
  if (modifiers.length > 0) {
    payload.modifiers = modifiers;
  }
  return payload;
};

const buildModifierQtyPatchPayload = ({ line, nextQty, modifierOptions }) => {
  const baseModifiers = resolveLineModifiersForPayload(
    line?.modifiers,
    modifierOptions,
    line?.qty,
  );

  return {
    qty: nextQty,
    modifiers: baseModifiers,
  };
};

const TakeawayOrderEditor = ({ entryVariant = "AUTO", showHeader = true }) => {
  const { orderId: routeOrderId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const { isDarkMode, toggleDarkMode } = useTheme();
  const resetInProgressRef = React.useRef(false);
  const openInProgressRef = React.useRef(false);
  const draftSyncPromiseRef = React.useRef(null);
  const pendingNavigateRef = React.useRef(null);
  const customerModalInitializedRef = React.useRef(false);
  const hydratedCustomerOrderIdRef = React.useRef(null);
  const [orderId, setOrderId] = useState(() => routeOrderId || null);
  const [isCreatingOrder, setIsCreatingOrder] = useState(false);

  const isFinalOrderStatus = React.useCallback((value) => {
    const normalized = String(value || "").trim().toUpperCase();
    return normalized === "COMPLETED" || normalized === "CANCELLED";
  }, []);

  React.useEffect(() => {
    setOrderId(routeOrderId || null);
  }, [routeOrderId]);

  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  const [showDiscount, setShowDiscount] = useState(false);
  const [showNotesModal, setShowNotesModal] = useState(false);
  const [showModifiers, setShowModifiers] = useState(false);
  const [showPayModal, setShowPayModal] = useState(false);
  const [showTakeawayCustomerModal, setShowTakeawayCustomerModal] =
    useState(false);
  const [isPreparingPayment, setIsPreparingPayment] = useState(false);
  const [activePaymentMethod, setActivePaymentMethod] = useState(null);
  const [heldOrdersModalOpen, setHeldOrdersModalOpen] = useState(false);
  const [takeawayCustomerName, setTakeawayCustomerName] = useState("");
  const [takeawayCustomerPhone, setTakeawayCustomerPhone] = useState("");
  const [takeawayCustomerEmail, setTakeawayCustomerEmail] = useState("");
  const [customerError, setCustomerError] = useState(null);
  const [customerErrorField, setCustomerErrorField] = useState(null);
  const takeawayCustomerPhoneInputRef = useRef(null);
  const takeawayCustomerNameInputRef = useRef(null);
  const takeawayCustomerEmailInputRef = useRef(null);

  const customerLookup = useCustomerLookup({
    phone: takeawayCustomerPhone,
    enabled: showTakeawayCustomerModal,
    minDigits: 10,
  });

  React.useEffect(() => {
    if (!showTakeawayCustomerModal) return;
    if (!customerLookup.customer) return;

    if (!takeawayCustomerName.trim() && customerLookup.customer.fullName) {
      setTakeawayCustomerName(String(customerLookup.customer.fullName).trim());
    }
    if (!takeawayCustomerEmail.trim() && customerLookup.customer.email) {
      setTakeawayCustomerEmail(String(customerLookup.customer.email).trim());
    }
  }, [
    showTakeawayCustomerModal,
    customerLookup.customer,
    takeawayCustomerName,
    takeawayCustomerEmail,
  ]);
  const [pendingCustomerAction, setPendingCustomerAction] = useState(null);
  const [activeTakeawayCustomerField, setActiveTakeawayCustomerField] =
    useState("phone");
  const [isRefreshingAfterPayment, setIsRefreshingAfterPayment] =
    useState(false);
  const [paymentProgress, setPaymentProgress] = useState({
    title: "Finalizing payment...",
    message: "Please wait while we complete the payment.",
    value: 20,
  });
  const [confirmModal, setConfirmModal] = useState({
    isOpen: false,
    action: null,
  });
  const [stationRequiredModalOpen, setStationRequiredModalOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [modifierPreset, setModifierPreset] = useState([]);
  const [skipUpsellForSelectedItem, setSkipUpsellForSelectedItem] =
    useState(false);
  const [notes, setNotes] = useState("");
  const [realtimeUpdate, setRealtimeUpdate] = useState(null);
  const [orderDuration, setOrderDuration] = useState("");
  const [upsellModal, setUpsellModal] = useState({
    isOpen: false,
    triggerItemName: "",
    triggerItemPrice: null,
    suggestions: [],
  });
  const [categoryFilter, setCategoryFilter] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const lastInvoicePaymentAttemptRef = useRef(null);
  const cashSessionGuard = useSubmitCashSessionGuard();

  const resetEditorState = React.useCallback(() => {
    pendingNavigateRef.current = null;
    openInProgressRef.current = false;

    setOrderId(null);
    setIsCreatingOrder(false);

    setError(null);
    setSuccessMessage(null);
    setRealtimeUpdate(null);
    setOrderDuration("");

    setShowDiscount(false);
    setShowNotesModal(false);
    setShowModifiers(false);
    setShowPayModal(false);
    setShowTakeawayCustomerModal(false);

    setTakeawayCustomerName("");
    setTakeawayCustomerPhone("");
    setTakeawayCustomerEmail("");
    hydratedCustomerOrderIdRef.current = null;
    setCustomerError(null);
    setCustomerErrorField(null);
    setPendingCustomerAction(null);
    setActiveTakeawayCustomerField("name");

    setIsRefreshingAfterPayment(false);
    setConfirmModal({ isOpen: false, action: null });

    setSelectedItem(null);
    setModifierPreset([]);
    setSkipUpsellForSelectedItem(false);
    setNotes("");

    setUpsellModal({
      isOpen: false,
      triggerItemName: "",
      triggerItemPrice: null,
      suggestions: [],
    });

    setCategoryFilter("");
    setSearchQuery("");
  }, []);

  const resetToNewOrder = React.useCallback(
    (reason = "") => {
      if (resetInProgressRef.current) return;
      resetInProgressRef.current = true;

      resetEditorState();

      const path = String(location?.pathname || "");
      if (path.startsWith("/pos/takeaway")) {
        navigate("/pos/takeaway", { replace: true });
        setTimeout(() => {
          resetInProgressRef.current = false;
        }, 0);
        return;
      }

      if (path.startsWith("/pos/dinein")) {
        navigate("/pos/dinein", { replace: true });
        setTimeout(() => {
          resetInProgressRef.current = false;
        }, 0);
        return;
      }

      navigate("/pos", { replace: true });
      setTimeout(() => {
        resetInProgressRef.current = false;
      }, 0);
    },
    [navigate, location?.pathname, resetEditorState],
  );

  // Get POS payment mode setting
  const { posPaymentMode, tipConfig } = useStationFeature();

  React.useEffect(() => {
    if (!showTakeawayCustomerModal) {
      customerModalInitializedRef.current = false;
      return;
    }

    if (customerModalInitializedRef.current) return;
    customerModalInitializedRef.current = true;

    const phoneValid = takeawayCustomerPhone.length === 10;
    const nameEmpty = !takeawayCustomerName.trim();
    const emailEmpty = !takeawayCustomerEmail.trim();

    if (!phoneValid) {
      setActiveTakeawayCustomerField("phone");
      return;
    }
    if (nameEmpty) {
      setActiveTakeawayCustomerField("name");
      return;
    }
    if (emailEmpty) {
      setActiveTakeawayCustomerField("email");
      return;
    }
    setActiveTakeawayCustomerField("phone");
  }, [
    showTakeawayCustomerModal,
    takeawayCustomerName,
    takeawayCustomerPhone,
    takeawayCustomerEmail,
  ]);

  React.useEffect(() => {
    if (!showTakeawayCustomerModal) return;

    const input =
      activeTakeawayCustomerField === "phone"
        ? takeawayCustomerPhoneInputRef.current
        : activeTakeawayCustomerField === "name"
          ? takeawayCustomerNameInputRef.current
          : takeawayCustomerEmailInputRef.current;

    if (!input) return;

    const frame = window.requestAnimationFrame(() => {
      try {
        input.focus({ preventScroll: true });
      } catch {
        input.focus();
      }
    });

    return () => window.cancelAnimationFrame(frame);
  }, [activeTakeawayCustomerField, showTakeawayCustomerModal]);

  const getActiveTakeawayCustomerValue = () => {
    if (activeTakeawayCustomerField === "phone") return takeawayCustomerPhone;
    if (activeTakeawayCustomerField === "email") return takeawayCustomerEmail;
    return takeawayCustomerName;
  };

  const handleTakeawayCustomerPhoneChange = (nextValue) => {
    const nextPhone = sanitizeCustomerPhone(nextValue);
    if (nextPhone !== takeawayCustomerPhone) {
      setTakeawayCustomerName("");
      setTakeawayCustomerEmail("");
    }
    setTakeawayCustomerPhone(nextPhone);
    setCustomerError(null);
    setCustomerErrorField(null);
    if (nextPhone.length === 10) {
      setActiveTakeawayCustomerField("name");
    }
  };

  const setActiveTakeawayCustomerValue = (nextValue) => {
    if (activeTakeawayCustomerField === "phone") {
      handleTakeawayCustomerPhoneChange(nextValue);
      return;
    }
    if (!isTakeawayCustomerPhoneValid) {
      ensureValidTakeawayPhoneBeforeAdvance();
      return;
    }
    if (activeTakeawayCustomerField === "email") {
      setTakeawayCustomerEmail(nextValue);
      if (customerErrorField === "email") {
        setCustomerError(null);
        setCustomerErrorField(null);
      }
      return;
    }
    setTakeawayCustomerName(nextValue);
    if (customerErrorField === "name") {
      setCustomerError(null);
      setCustomerErrorField(null);
    }
  };

  const ensureValidTakeawayPhoneBeforeAdvance = () => {
    const phoneError = validateCustomerPhone(takeawayCustomerPhone, {
      required: true,
    });
    if (phoneError) {
      setCustomerError(phoneError);
      setCustomerErrorField("phone");
      setActiveTakeawayCustomerField("phone");
      return false;
    }
    return true;
  };

  const isTakeawayCustomerPhoneValid = takeawayCustomerPhone.length === 10;

  const handleTakeawayCustomerKeyboardDone = () => {
    if (activeTakeawayCustomerField === "phone") {
      if (!ensureValidTakeawayPhoneBeforeAdvance()) return;
      setActiveTakeawayCustomerField("name");
      return;
    }
    if (activeTakeawayCustomerField === "name") {
      setActiveTakeawayCustomerField("email");
      return;
    }
    handleTakeawayPay();
  };

  const handleCloseTakeawayCustomerModal = () => {
    setShowTakeawayCustomerModal(false);
    setTakeawayCustomerName("");
    setTakeawayCustomerPhone("");
    setTakeawayCustomerEmail("");
    setCustomerError(null);
    setCustomerErrorField(null);
    setPendingCustomerAction(null);
    setActiveTakeawayCustomerField("phone");
    customerModalInitializedRef.current = false;
  };

  // Calculate order duration
  const calculateDuration = (createdAt) => {
    if (!createdAt) return "";
    const now = new Date();
    const created = new Date(createdAt);
    const diffMs = now - created;
    const diffMins = Math.floor(diffMs / 60000);

    if (diffMins < 60) {
      return `${diffMins} min`;
    }
    const hours = Math.floor(diffMins / 60);
    const mins = diffMins % 60;
    return `${hours}h ${mins}m`;
  };

  // Real-time updates for this order
  useOrderRealtime(orderId, (payload) => {
    setRealtimeUpdate(payload);
    setTimeout(() => setRealtimeUpdate(null), 3000);

    const nextStatus =
      payload?.status ||
      payload?.orderStatus ||
      payload?.order_state ||
      payload?.order?.status ||
      "";
    if (isFinalOrderStatus(nextStatus)) {
      setError("Order completed. Starting a new order...");
      resetToNewOrder("realtime_status");
    }
  });

  // Fetch order
  const {
    data: fetchedOrder,
    isLoading,
    error: orderError,
    refetch,
  } = useGetPosOrderQuery(orderId ?? skipToken);

  const fallbackOrder = React.useMemo(() => {
    const path = String(location?.pathname || "");
    const isTakeawayPath = path.startsWith("/pos/takeaway");

    return {
      orderId: null,
      seatId: null,
      seatRef: "",
      status: "DRAFT",
      orderType: isTakeawayPath ? "TAKEAWAY" : "DINEIN",
      rowVersion: 0,
      createdAt: null,
      notes: "",
      lines: [],
      invoices: [],
      totals: {
        subTotal: 0,
        tax: 0,
        grandTotal: 0,
        paid: 0,
      },
      discounts: [],
      customerName: "",
      customerPhone: "",
      customerEmail: "",
    };
  }, [location?.pathname]);

  const activeFetchedOrder = React.useMemo(() => {
    if (!orderId || !fetchedOrder) return null;
    return String(fetchedOrder.orderId || "") === String(orderId)
      ? fetchedOrder
      : null;
  }, [fetchedOrder, orderId]);

  const order = orderId ? activeFetchedOrder || fallbackOrder : fallbackOrder;
  const [localDraftLines, setLocalDraftLines] = useState([]);
  const [orderOverride, setOrderOverride] = useState(null);
  const [showPostSubmitProcessing, setShowPostSubmitProcessing] = useState(false);
  const effectiveOrder = orderOverride || order;
  const effectiveOrderLines = React.useMemo(
    () => effectiveOrder?.lines || [],
    [effectiveOrder],
  );
  const { data: taxRatesData } = useGetTaxRatesQuery();
  const draftTaxRate = React.useMemo(() => {
    const totals = effectiveOrder?.totals || {};
    const subTotal = Number(totals.subTotal || 0);
    const tax = Number(totals.tax || 0);
    return subTotal > 0 && tax >= 0 ? tax / subTotal : 0;
  }, [effectiveOrder]);
  const { data: upsellCampaignsData } = useGetUpsellCampaignsQuery();
  const { data: referenceData } = useGetCatalogReferenceQuery();

  React.useEffect(() => {
    if (!orderId) {
      setLocalDraftLines([]);
      setOrderOverride(null);
      return;
    }

    const storedDraft = readLocalDraft(orderId);
    if (storedDraft !== null) {
      setLocalDraftLines(storedDraft);
      return;
    }

    setLocalDraftLines(buildDraftStateFromOrder(effectiveOrderLines));
  }, [orderId, effectiveOrderLines]);

  React.useEffect(() => {
    if (!orderId) return;
    writeLocalDraft(orderId, localDraftLines);
  }, [localDraftLines, orderId]);

  const menuOrderType = React.useMemo(() => {
    const path = String(location?.pathname || "");
    if (path.startsWith("/pos/takeaway")) return "TAKEAWAY";
    if (path.startsWith("/pos/dinein")) return "DINEIN";
    return normalizeOrderTypeForEligibility(order?.orderType || "DINEIN");
  }, [location?.pathname, order?.orderType]);

  const {
    data: menuData,
    isLoading: isMenuLoading,
    refetch: refetchMenu,
  } = useGetPublicCatalogMenuQuery({
    orderType: menuOrderType,
  });
  const modifierOptions = React.useMemo(
    () =>
      Array.isArray(referenceData?.modifierOptions)
        ? referenceData.modifierOptions
        : [],
    [referenceData?.modifierOptions],
  );

  React.useEffect(() => {
    const refreshMenu = () => {
      refetchMenu();
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        refreshMenu();
      }
    };

    window.addEventListener("focus", refreshMenu);
    document.addEventListener("visibilitychange", handleVisibilityChange);
    const interval = setInterval(refreshMenu, 60000);

    return () => {
      window.removeEventListener("focus", refreshMenu);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      clearInterval(interval);
    };
  }, [refetchMenu]);

  React.useEffect(() => {
    hydratedCustomerOrderIdRef.current = null;
    setTakeawayCustomerName("");
    setTakeawayCustomerPhone("");
    setTakeawayCustomerEmail("");
    setCustomerError(null);
    setCustomerErrorField(null);
  }, [routeOrderId]);

  React.useEffect(() => {
    const activeOrderId = routeOrderId || orderId || null;
    if (!activeOrderId) {
      hydratedCustomerOrderIdRef.current = null;
      return;
    }
    if (hydratedCustomerOrderIdRef.current === activeOrderId) return;

    const resumedHeldOrder = location?.state?.resumedHeldOrder;
    const resumedMatches =
      resumedHeldOrder &&
      String(resumedHeldOrder.orderId || "") === String(activeOrderId);

    if (resumedMatches) {
      setTakeawayCustomerName(String(resumedHeldOrder.customerName || "").trim());
      setTakeawayCustomerPhone(
        sanitizeCustomerPhone(String(resumedHeldOrder.customerPhone || "").trim()),
      );
      setTakeawayCustomerEmail(String(resumedHeldOrder.customerEmail || "").trim());
      hydratedCustomerOrderIdRef.current = activeOrderId;
      return;
    }

    const pendingCustomer = readPendingCustomerFromStorage(activeOrderId);
    if (!activeFetchedOrder && !pendingCustomer) return;

    setTakeawayCustomerName(
      String(
        activeFetchedOrder?.customerName || pendingCustomer?.name || "",
      ).trim(),
    );
    setTakeawayCustomerPhone(
      sanitizeCustomerPhone(
        String(
          activeFetchedOrder?.customerPhone || pendingCustomer?.phone || "",
        ).trim(),
      ),
    );
    setTakeawayCustomerEmail(
      String(
        activeFetchedOrder?.customerEmail || pendingCustomer?.email || "",
      ).trim(),
    );
    hydratedCustomerOrderIdRef.current = activeOrderId;
  }, [activeFetchedOrder, location?.state, orderId, routeOrderId]);

  React.useEffect(() => {
    const activeOrderId = routeOrderId || orderId || null;
    if (!activeOrderId) return;
    if (
      !activeFetchedOrder?.customerName &&
      !activeFetchedOrder?.customerPhone &&
      !activeFetchedOrder?.customerEmail
    ) {
      return;
    }

    sessionStorage.removeItem(getPendingCustomerStorageKey(activeOrderId));
  }, [
    activeFetchedOrder?.customerEmail,
    activeFetchedOrder?.customerName,
    activeFetchedOrder?.customerPhone,
    orderId,
    routeOrderId,
  ]);

  // Mutations
  const [openTakeawayOrder] = useOpenPosTakeawayMutation();
  const [openDineInOrder] = useOpenPosDineInMutation();
  const [addLine, { isLoading: isAddingLine }] = useAddPosOrderLineMutation();
  const [updateNotes, { isLoading: isUpdatingNotes }] =
    useUpdatePosOrderNotesMutation();
  const [submitOrder, { isLoading: isSubmitting }] =
    useSubmitPosOrderMutation();
  const [
    processOrderPayment,
    { isLoading: isProcessingOrderPayment, error: orderPaymentError },
  ] = useProcessPosOrderPaymentMutation();
  const [
    processInvoicePayment,
    { isLoading: isProcessingInvoicePayment, error: invoicePaymentError },
  ] = useProcessPosInvoicePaymentMutation();
  const [updateOrderCustomer, { isLoading: isUpdatingCustomer }] =
    useUpdatePosOrderCustomerMutation();
  const user = useSelector((state) => state.auth.user);
  const {
    features,
    isLoading: isStoreFeaturesLoading,
  } = useStoreFeatures(true);
  const posDineInMode = features?.posDineInMode
    ? String(features.posDineInMode).toUpperCase()
    : "";
  const posEnableTakeaway = features?.posEnableTakeaway !== false;
  const adminPath = useMemo(
    () => getPosAdminDestination(user, posDineInMode),
    [user, posDineInMode],
  );
  const showAdmin = Boolean(adminPath);
  const showCashManagementButtons = posDineInMode !== "TABLE_DINE_IN";
  const requireCustomerDetails = Boolean(
    features?.requireCustomerDetails ?? features?.RequireCustomerDetails,
  );
  const isOrderBlocked = isChannelOrderingBlocked(features, "POS");
  const [closeOrder] = useClosePosOrderMutation();
  const [fetchOrderInvoices] = useLazyGetPosOrderInvoicesQuery();
  const [updateLine] = useUpdatePosOrderLineMutation();
  const [deleteLine] = useDeletePosOrderLineMutation();
  const [sendSeatHeartbeat] = usePosSeatHeartbeatMutation();
  const invokeAddLine = React.useCallback(
    ({ orderId: targetOrderId, payload }) =>
      addLine({
        orderId: targetOrderId,
        payload,
      }).unwrap(),
    [addLine],
  );
  const invokeUpdateLine = React.useCallback(
    ({ orderId: targetOrderId, lineId, payload }) =>
      updateLine({
        orderId: targetOrderId,
        lineId,
        payload,
      }).unwrap(),
    [updateLine],
  );
  const invokeDeleteLine = React.useCallback(
    ({ orderId: targetOrderId, lineId }) =>
      deleteLine({
        orderId: targetOrderId,
        lineId,
      }).unwrap(),
    [deleteLine],
  );

  const isProcessingPayment =
    isProcessingOrderPayment || isProcessingInvoicePayment;
  const paymentError = invoicePaymentError || orderPaymentError;

  const resolveOpenVariant = React.useCallback(() => {
    const normalizedEntryVariant = String(entryVariant || "AUTO")
      .trim()
      .toUpperCase();

    if (normalizedEntryVariant === "TAKEAWAY") return "TAKEAWAY";
    if (normalizedEntryVariant === "DINE_IN") return "DINE_IN";

    const path = String(location?.pathname || "");
    if (path.startsWith("/pos/takeaway")) return "TAKEAWAY";
    if (path.startsWith("/pos/dinein")) return "DINE_IN";

    return "DINE_IN";
  }, [entryVariant, location?.pathname]);

  const ensureOrderId = React.useCallback(async () => {
    if (orderId) return orderId;
    if (openInProgressRef.current) return null;

    if (isStoreFeaturesLoading && !features) {
      setError("Checking order availability. Please try again in a moment.");
      return null;
    }

    if (isOrderBlocked) {
      return null;
    }

    openInProgressRef.current = true;
    setIsCreatingOrder(true);
    setError(null);

    try {
      const variant = resolveOpenVariant();
      const payload = { channel: "POS", sourceSystem: "EDGE" };
      const result =
        variant === "TAKEAWAY"
          ? await openTakeawayOrder(payload).unwrap()
          : await openDineInOrder(payload).unwrap();

      const nextOrderId = result?.orderId || null;
      if (!nextOrderId) {
        setError("Unable to start a new order. Please try again.");
        return null;
      }

      setOrderId(nextOrderId);
      // Keep takeaway on the current route so the first item add does not
      // remount the editor and feel like a page refresh.
      pendingNavigateRef.current =
        variant === "DINE_IN" ? `/pos/dinein/${nextOrderId}` : null;

      return nextOrderId;
    } catch (err) {
      if (isOrderingBlockedError(err)) {
        dispatch(api.util.invalidateTags(["StoreFeatures"]));
      }
      setError(getPosErrorMessage(err));
      return null;
    } finally {
      setIsCreatingOrder(false);
      openInProgressRef.current = false;
    }
  }, [
    orderId,
    resolveOpenVariant,
    openTakeawayOrder,
    openDineInOrder,
    features,
    isOrderBlocked,
    isStoreFeaturesLoading,
    dispatch,
  ]);

  const syncPendingDraftLines = React.useCallback(async () => {
    if (!orderId) return effectiveOrder;

    if (draftSyncPromiseRef.current) {
      return await draftSyncPromiseRef.current;
    }

    const runSync = (async () => {
      await syncLocalDraftToServer({
        orderId,
        orderLines: effectiveOrderLines,
        draftLines: localDraftLines,
        addLine: invokeAddLine,
        updateLine: invokeUpdateLine,
        deleteLine: invokeDeleteLine,
        buildLineQtyUpdatePayload,
        buildModifierQtyPatchPayload,
        modifierOptions,
      });

      clearLocalDraft(orderId);
      const refreshed = await refetch();
      const updatedOrder = refreshed?.data || effectiveOrder;
      setOrderOverride(updatedOrder || null);
      setLocalDraftLines(buildDraftStateFromOrder(updatedOrder?.lines || []));
      return updatedOrder;
    })();

    draftSyncPromiseRef.current = runSync;

    try {
      return await runSync;
    } finally {
      if (draftSyncPromiseRef.current === runSync) {
        draftSyncPromiseRef.current = null;
      }
    }
  }, [
    effectiveOrder,
    orderId,
    effectiveOrderLines,
    localDraftLines,
    invokeAddLine,
    invokeUpdateLine,
    invokeDeleteLine,
    refetch,
    modifierOptions,
  ]);

  const refetchLatestOrder = React.useCallback(async () => {
    const refreshed = await refetch();
    const latestOrder = refreshed?.data || null;
    if (latestOrder) {
      setOrderOverride(latestOrder);
    }
    return latestOrder;
  }, [refetch]);

  const runWithStaleRetry = React.useCallback(
    async (operation, options = {}) => {
      const { attempts = 3, pauseMs = 250 } = options;
      let lastError = null;

      for (let attempt = 0; attempt < attempts; attempt += 1) {
        try {
          return await operation();
        } catch (err) {
          const errorCode = err?.data?.code || err?.data?.errorCode;
          if (errorCode !== POS_ERROR_CODES.CONFLICT_STALE_VERSION) {
            throw err;
          }

          lastError = err;
          await refetchLatestOrder();
          await sleep(pauseMs);
        }
      }

      throw lastError;
    },
    [refetchLatestOrder],
  );

  const isOrderLockedError = React.useCallback((err) => {
    const code = err?.data?.code || err?.data?.errorCode || err?.data?.title;
    return (
      err?.status === 409 &&
      String(code || "")
        .trim()
        .toUpperCase() === "ORDER_LOCKED"
    );
  }, []);

  const handleOrderLockedError = React.useCallback(
    async (err) => {
      if (!isOrderLockedError(err)) return false;

      try {
        await refetch();
      } catch {
        // ignore refetch errors; we'll still reset the flow
      }

      setError(getPosErrorMessage(err));
      setTimeout(() => resetToNewOrder("order_locked"), 600);
      return true;
    },
    [isOrderLockedError, refetch, resetToNewOrder],
  );

  // Handle order error
  React.useEffect(() => {
    if (orderError) {
      const message = getPosErrorMessage(orderError);
      setError(message);

      if (shouldNavigateToSeatBoard(orderError)) {
        setTimeout(() => navigate("/pos"), 2000);
      }
    }
  }, [orderError, navigate]);

  // Initialize notes from order
  React.useEffect(() => {
    if (order?.notes) {
      setNotes(order.notes);
    }
  }, [order]);

  React.useEffect(() => {
    if (!order) return;
    if (isFinalOrderStatus(order?.status)) {
      resetToNewOrder("order_status");
    }
  }, [order, order?.status, isFinalOrderStatus, resetToNewOrder]);

  // Keep draft seat session alive while editor is open
  React.useEffect(() => {
    if (!order?.seatId) return undefined;
    if (String(order?.status || "").toUpperCase() !== "DRAFT") return undefined;

    let isActive = true;
    const heartbeat = () => {
      if (!isActive) return;
      sendSeatHeartbeat(order.seatId).unwrap().catch(() => {});
    };

    heartbeat();
    const interval = setInterval(heartbeat, 45000);

    return () => {
      isActive = false;
      clearInterval(interval);
    };
  }, [order?.seatId, order?.status, sendSeatHeartbeat]);

  // Update order duration every minute
  React.useEffect(() => {
    if (order?.createdAt) {
      setOrderDuration(calculateDuration(order.createdAt));

      const interval = setInterval(() => {
        setOrderDuration(calculateDuration(order.createdAt));
      }, 60000);

      return () => clearInterval(interval);
    }
  }, [order?.createdAt]);

  const upsellCampaigns = React.useMemo(
    () => (Array.isArray(upsellCampaignsData) ? upsellCampaignsData : []),
    [upsellCampaignsData],
  );
  const allItems = React.useMemo(
    () => (Array.isArray(menuData?.items) ? menuData.items : []),
    [menuData?.items],
  );
  const categories = React.useMemo(
    () => (Array.isArray(menuData?.categories) ? menuData.categories : []),
    [menuData?.categories],
  );
  const activeCategories = React.useMemo(
    () => categories.filter((category) => category?.isActive !== false),
    [categories],
  );
  const activeCategoryIds = React.useMemo(
    () => new Set(activeCategories.map((category) => String(category.id))),
    [activeCategories],
  );
  const activeParentCategories = React.useMemo(
    () =>
      activeCategories
        .filter((category) => !category.parentCategoryId)
        .sort((a, b) => (a.sort || 0) - (b.sort || 0)),
    [activeCategories],
  );
  const categoriesById = React.useMemo(() => {
    const map = new Map();
    categories.forEach((category) => {
      if (category?.id) {
        map.set(String(category.id), category);
      }
    });
    return map;
  }, [categories]);
  const itemsById = React.useMemo(() => {
    const map = new Map();
    allItems.forEach((item) => {
      if (item?.id) {
        map.set(item.id, item);
      }
    });
    return map;
  }, [allItems]);

  const filteredItems = React.useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    const hasCategoryMetadata = categories.length > 0;
    const normalizedCategoryFilter = String(categoryFilter || "");
    const selectedCategory = normalizedCategoryFilter
      ? categoriesById.get(normalizedCategoryFilter) || null
      : null;
    const childCategoryIds = new Set(
      activeCategories
        .filter(
          (category) =>
            String(category?.parentCategoryId || "") === normalizedCategoryFilter,
        )
        .map((category) => String(category.id)),
    );
    const hasChildCategories =
      normalizedCategoryFilter &&
      !selectedCategory?.parentCategoryId &&
      childCategoryIds.size > 0;

    return allItems
      .filter((item) => isItemEligibleForOrderType(item, menuOrderType))
      .filter((item) =>
        hasCategoryMetadata
          ? activeCategoryIds.has(String(item?.categoryId || ""))
          : true,
      )
      .filter((item) =>
        normalizedCategoryFilter
          ? hasChildCategories
            ? String(item?.categoryId || "") === normalizedCategoryFilter ||
              childCategoryIds.has(String(item?.categoryId || ""))
            : item.categoryId === categoryFilter
          : true,
      )
      .filter((item) => {
        if (!query) return true;
        const haystack =
          `${item.name || ""} ${item.description || ""}`.toLowerCase();
        return haystack.includes(query);
      });
  }, [
    allItems,
    categoryFilter,
    searchQuery,
    activeCategoryIds,
    activeCategories,
    categoriesById,
    categories.length,
    menuOrderType,
  ]);

  const visibleCategoryIds = React.useMemo(() => {
    const ids = new Set();
    allItems
      .filter((item) => isItemEligibleForOrderType(item, menuOrderType))
      .filter((item) =>
        categories.length > 0
          ? activeCategoryIds.has(String(item?.categoryId || ""))
          : true,
      )
      .forEach((item) => {
        if (item?.categoryId) {
          ids.add(String(item.categoryId));
        }
      });
    return ids;
  }, [allItems, activeCategoryIds, categories.length, menuOrderType]);
  const visibleParentCategoryIds = React.useMemo(() => {
    const ids = new Set();
    visibleCategoryIds.forEach((categoryId) => {
      ids.add(String(categoryId));
      const parentCategoryId = categoriesById.get(String(categoryId))?.parentCategoryId;
      if (parentCategoryId) {
        ids.add(String(parentCategoryId));
      }
    });
    activeCategories.forEach((category) => {
      if (category?.parentCategoryId) {
        ids.add(String(category.parentCategoryId));
      }
    });
    return ids;
  }, [activeCategories, categoriesById, visibleCategoryIds]);

  const visibleParentCategories = React.useMemo(
    () =>
      activeParentCategories.filter((category) =>
        visibleParentCategoryIds.has(String(category.id)),
      ),
    [activeParentCategories, visibleParentCategoryIds],
  );
  const selectedCategory = React.useMemo(
    () => categoriesById.get(String(categoryFilter || "")) || null,
    [categoriesById, categoryFilter],
  );
  const activeRailCategoryId =
    selectedCategory?.parentCategoryId || categoryFilter;
  const visibleChildCategories = React.useMemo(
    () =>
      activeCategories
        .filter(
          (category) =>
            String(category?.parentCategoryId || "") === String(categoryFilter || ""),
        )
        .sort((a, b) => (a.sort || 0) - (b.sort || 0)),
    [activeCategories, categoryFilter],
  );
  const selectedParentItems = React.useMemo(
    () =>
      !searchQuery &&
      categoryFilter &&
      !selectedCategory?.parentCategoryId
        ? filteredItems.filter(
            (item) => String(item?.categoryId || "") === String(categoryFilter),
          )
        : [],
    [categoryFilter, filteredItems, searchQuery, selectedCategory],
  );
  const showParentCategoryBrowser =
    !searchQuery &&
    !!categoryFilter &&
    !selectedCategory?.parentCategoryId &&
    (visibleChildCategories.length > 0 || selectedParentItems.length > 0);
  const showChildCategorySection =
    showParentCategoryBrowser && visibleChildCategories.length > 0;
  const showParentItemSection =
    showParentCategoryBrowser && selectedParentItems.length > 0;
  const useCategorySectionHeading =
    showChildCategorySection && showParentItemSection;
  const useItemSectionHeading =
    showChildCategorySection && showParentItemSection;
  const visibleSelectableCategoryIds = React.useMemo(() => {
    const ids = new Set(visibleParentCategoryIds);
    visibleChildCategories.forEach((category) => {
      ids.add(String(category.id));
    });
    if (selectedCategory?.id) {
      ids.add(String(selectedCategory.id));
    }
    if (selectedCategory?.parentCategoryId) {
      ids.add(String(selectedCategory.parentCategoryId));
    }
    return ids;
  }, [selectedCategory, visibleChildCategories, visibleParentCategoryIds]);

  React.useEffect(() => {
    if (categoryFilter && !visibleSelectableCategoryIds.has(String(categoryFilter))) {
      setCategoryFilter("");
    }
  }, [categoryFilter, visibleSelectableCategoryIds]);

  const selectedItemModifierGroups = React.useMemo(
    () =>
      resolveItemModifierGroups(
        selectedItem?.modifierGroups || [],
        modifierOptions,
      ),
    [selectedItem, modifierOptions],
  );

  const isLineNonTaxable = React.useCallback(
    (line) => {
      if (line?.isTaxable === false || line?.taxable === false) return true;
      const itemId = line?.itemId || line?.productId || null;
      if (!itemId) return false;
      const item = itemsById.get(itemId);
      return item?.isTaxable === false || item?.taxable === false;
    },
    [itemsById],
  );
  const activeTaxRate = React.useMemo(
    () =>
      (Array.isArray(taxRatesData) ? taxRatesData : [])
        .filter((taxRate) => taxRate?.isActive !== false)
        .reduce(
          (sum, taxRate) =>
            sum + toRateFraction(taxRate?.ratePercent ?? taxRate?.rate ?? 0),
          0,
        ),
    [taxRatesData],
  );

  const openUpsellSuggestions = React.useCallback(
    (triggerItem) => {
      if (!triggerItem?.id) return;

      const matchedCampaigns = upsellCampaigns
        .filter(
          (campaign) =>
            campaign?.isActive &&
            Array.isArray(campaign.triggerItemIds) &&
            campaign.triggerItemIds.includes(triggerItem.id),
        )
        .sort((a, b) => Number(b.priority || 0) - Number(a.priority || 0));

      if (matchedCampaigns.length === 0) return;

      const seenIds = new Set();
      const suggestions = [];

      for (const campaign of matchedCampaigns) {
        const orderedUpsells = Array.isArray(campaign.upsellItems)
          ? [...campaign.upsellItems].sort(
              (a, b) => Number(a.sortOrder || 0) - Number(b.sortOrder || 0),
            )
          : [];

        for (const upsellItem of orderedUpsells) {
          const itemId = upsellItem?.itemId;
          if (
            !itemId ||
            itemId === triggerItem.id ||
            seenIds.has(itemId) ||
            !itemsById.has(itemId)
          ) {
            continue;
          }

          const item = itemsById.get(itemId);
          if (item?.isAvailable === false) continue;

          suggestions.push(item);
          seenIds.add(itemId);

          if (suggestions.length >= 6) break;
        }

        if (suggestions.length >= 6) break;
      }

      if (suggestions.length === 0) return;

      setUpsellModal({
        isOpen: true,
        triggerItemName: triggerItem.name || "",
        triggerItemPrice: Number.isFinite(Number(triggerItem.basePrice))
          ? Number(triggerItem.basePrice)
          : null,
        suggestions,
      });
    },
    [itemsById, upsellCampaigns],
  );

  const addItemToOrder = async (
    item,
    modifiers = [],
    { showUpsell = true } = {},
  ) => {
    if (!item?.id) return;
    if (isFinalOrderStatus(order?.status)) {
      setError("Order is no longer editable. Starting a new order...");
      resetToNewOrder("final_state");
      return;
    }

    try {
      const ensuredOrderId = await ensureOrderId();
      if (!ensuredOrderId) return;

      const flushPendingNavigation = () => {
        const nextPath = pendingNavigateRef.current;
        if (!nextPath) return;
        pendingNavigateRef.current = null;
        navigate(nextPath, { replace: true });
      };

      setLocalDraftLines((currentLines) => {
        const nextDraftLines = addItemToLocalDraft({
          draftLines: currentLines,
          item,
          modifiers,
          fallbackTaxRate: draftTaxRate,
        });

        // Persist immediately for newly opened takeaway orders so the
        // route-change hydration effect does not wipe the first added item.
        if (ensuredOrderId) {
          writeLocalDraft(ensuredOrderId, nextDraftLines);
        }

        return nextDraftLines;
      });

      setSelectedItem(null);
      setShowModifiers(false);

      flushPendingNavigation();

      if (showUpsell) {
        openUpsellSuggestions(item);
      }
    } catch (err) {
      if (isItemStationRequiredError(err)) {
        setStationRequiredModalOpen(true);
        return;
      }
      if (await handleOrderLockedError(err)) return;
      setError(getPosErrorMessage(err));
    }
  };

  const handleItemSelect = (item) => {
    if (item?.isAvailable === false) return;

    setSelectedItem(item);
    setModifierPreset([]);
    setSkipUpsellForSelectedItem(false);

    if (itemHasSelectableModifiers(item.modifierGroups, modifierOptions)) {
      setShowModifiers(true);
    } else {
      addItemToOrder(item, [], { showUpsell: true });
    }
  };

  const handleAddItem = async (modifiers) => {
    if (!selectedItem) return;

    const modifierLineItems = splitModifiersIntoLineItems(modifiers);

    for (let index = 0; index < modifierLineItems.length; index += 1) {
      await addItemToOrder(selectedItem, modifierLineItems[index], {
        showUpsell: index === 0 ? !skipUpsellForSelectedItem : false,
      });
    }

    setSkipUpsellForSelectedItem(false);
  };

  const handleConfirmUpsellItems = async (items = []) => {
    setUpsellModal({
      isOpen: false,
      triggerItemName: "",
      triggerItemPrice: null,
      suggestions: [],
    });

    if (!Array.isArray(items) || items.length === 0) {
      return;
    }

    for (const entry of items) {
      const item = entry?.item;
      const qty = Math.max(1, Number(entry?.qty || 1));
      if (!item) continue;

      for (let i = 0; i < qty; i += 1) {
        await addItemToOrder(item, [], { showUpsell: false });
      }
    }
  };

  const handleUpdateNotes = async () => {
    if (isFinalOrderStatus(order?.status)) {
      setError("Order is no longer editable. Starting a new order...");
      resetToNewOrder("final_state");
      return;
    }
    try {
      await updateNotes({
        orderId,
        payload: { notes },
      }).unwrap();
      setShowNotesModal(false);
    } catch (err) {
      if (await handleOrderLockedError(err)) return;
      setError(getPosErrorMessage(err));
    }
  };

  const handleSubmit = () => {
    const unsentLines = pendingDisplayLines;
    if (unsentLines.length === 0) {
      setError("No new items to submit. Please add items first.");
      return;
    }

    if (!cashSessionGuard.ensureActiveCashSession()) return;

    if (requireCustomerDetails) {
      const name = String(order?.customerName || "").trim();
      const phone = String(order?.customerPhone || "").trim();
      const email = String(order?.customerEmail || "").trim();
      if (!name || !phone || !email) {
        setCustomerError(null);
        setPendingCustomerAction("SUBMIT");
        setShowTakeawayCustomerModal(true);
        return;
      }
    }

    // Check PAY_FIRST mode requirement
    if (isPayFirstMode && pendingItemsNeedPayment) {
      setError(
        `Payment required. Please pay $${unpaidAmount.toFixed(2)} before submitting to kitchen.`,
      );
      return;
    }

    setConfirmModal({
      isOpen: true,
      action: "SUBMIT_ORDER",
    });
  };

  const handleRequestClose = () => {
    if (isDraft && pendingDisplayLines.length > 0) {
      setConfirmModal({
        isOpen: true,
        action: "CLOSE_ORDER",
      });
      return;
    }

    void handleExitToSeatBoard();
  };

  const performSubmit = async () => {
    let updatedOrder = order;
    setShowPostSubmitProcessing(true);
    try {
      updatedOrder = await runWithStaleRetry(() => syncPendingDraftLines());
    } catch (err) {
      if (await handleOrderLockedError(err)) {
        setShowPostSubmitProcessing(false);
        return;
      }
      if (isOrderingBlockedError(err)) {
        dispatch(api.util.invalidateTags(["StoreFeatures"]));
      }
      setShowPostSubmitProcessing(false);
      setError(getPosErrorMessage(err));
      return;
    }

    try {
      const submitLatestOrder = async () => {
        const latestOrder =
          (await refetchLatestOrder()) || updatedOrder || effectiveOrder || null;

        return await submitOrder({
          orderId,
          payload: {
            expectedRowVersion:
              latestOrder?.rowVersion ?? updatedOrder?.rowVersion,
            submitRequestId: generateUUID(),
          },
        }).unwrap();
      };

      const result = await runWithStaleRetry(submitLatestOrder);

      clearLocalDraft(orderId);
      const submittedOrder = buildSubmittedOrderSnapshot(
        result?.order || updatedOrder || effectiveOrder || null,
      );
      setOrderOverride(submittedOrder);
      setLocalDraftLines([]);

      const { deltaLineCount } = result;

      if (deltaLineCount > 0) {
        setSuccessMessage(
          `Sent ${deltaLineCount} item change${deltaLineCount !== 1 ? "s" : ""} to kitchen`,
        );
      } else {
        setSuccessMessage("No new kitchen changes");
      }

      // After submitting to kitchen, return to Dining Room Control.
      setTimeout(() => {
        setSuccessMessage(null);
        setShowPostSubmitProcessing(false);
        cartLeaveGuard.allowNextNavigation();
        navigate("/pos");
      }, 1500);
    } catch (err) {
      setShowPostSubmitProcessing(false);
      const errorCode = err?.data?.code || err?.data?.errorCode;
      if (isOrderingBlockedError(err)) {
        dispatch(api.util.invalidateTags(["StoreFeatures"]));
      }

      if (isItemStationRequiredError(err)) {
        setStationRequiredModalOpen(true);
        setShowPostSubmitProcessing(false);
        return;
      }

      if (isPaymentConfirmationRequiredError(err)) {
        setError(
          "Payment confirmation is required before submitting to kitchen. Please complete the payment confirmation and try again.",
        );
        setShowPostSubmitProcessing(false);
        return;
      }

      if (errorCode === POS_ERROR_CODES.CONFLICT_STALE_VERSION) {
        setShowPostSubmitProcessing(false);
        setError(getPosErrorMessage(err));
        return;
      }

      if (await handleOrderLockedError(err)) {
        setShowPostSubmitProcessing(false);
        return;
      }
      setShowPostSubmitProcessing(false);
      setError(getPosErrorMessage(err));

      if (shouldNavigateToSeatBoard(err)) {
        setTimeout(() => navigate("/pos"), 2000);
      }
    }
  };

  const handleConfirmAction = async () => {
    const { action } = confirmModal;
    setConfirmModal({ isOpen: false, action: null });

    if (action === "SUBMIT_ORDER") {
      if (!cashSessionGuard.ensureActiveCashSession()) return;
      await performSubmit();
      return;
    }

    if (action === "CLOSE_ORDER") {
      await handleExitToSeatBoard();
    }
  };
  const handlePayment = async () => {
    if (!hasCartItems) return;
    if (isPayFirstMode) {
      setIsPreparingPayment(true);
      setPaymentProgress({
        title: "Processing order...",
        message: "Please wait while we prepare the order for payment.",
        value: 20,
      });
    }
    try {
      await runWithStaleRetry(() => syncPendingDraftLines());
      if (isPayFirstMode) {
        setPaymentProgress({
          title: "Opening payment...",
          message: "Payment options are almost ready.",
          value: 90,
        });
      }
    } catch (err) {
      setIsPreparingPayment(false);
      if (await handleOrderLockedError(err)) return;
      if (isOrderingBlockedError(err)) {
        dispatch(api.util.invalidateTags(["StoreFeatures"]));
      }
      setError(getPosErrorMessage(err));
      return;
    }

    try {
      const latestOrder = await refetchLatestOrder();
      if (latestOrder) {
        setOrderOverride(latestOrder);
        const latestRemainingBalance = getRemainingBalance(
          latestOrder,
          grandTotalAmount,
          totalPaid,
        );
        if (latestRemainingBalance <= 0) {
          setShowPayModal(false);
          setSuccessMessage("No balance due.");
          setIsPreparingPayment(false);
          return;
        }
      }
    } catch (err) {
      setIsPreparingPayment(false);
      if (await handleOrderLockedError(err)) return;
      if (isOrderingBlockedError(err)) {
        dispatch(api.util.invalidateTags(["StoreFeatures"]));
      }
      setError(getPosErrorMessage(err));
      return;
    }

    setIsPreparingPayment(false);
    setShowPayModal(true);
  };

  const handleTakeawaySubmit = async () => {
    if (!hasCartItems) return;
    if (isPreparingPayment || isRefreshingAfterPayment) return;

    if (!cashSessionGuard.ensureActiveCashSession()) return;

    if (requireCustomerDetails) {
      const name = String(order?.customerName || "").trim();
      const phone = String(order?.customerPhone || "").trim();
      if (!name || !phone) {
        setCustomerError(null);
        setCustomerErrorField(null);
        setPendingCustomerAction("PAY");
        setShowTakeawayCustomerModal(true);
        return;
      }
    }

    setIsPreparingPayment(true);
    setPaymentProgress({
      title: "Processing order...",
      message: "Please wait while we open the payment modal.",
      value: 20,
    });

    try {
      await syncPendingDraftLines();
    } catch (err) {
      setIsPreparingPayment(false);
      if (await handleOrderLockedError(err)) return;
      if (isOrderingBlockedError(err)) {
        dispatch(api.util.invalidateTags(["StoreFeatures"]));
      }
      setError(getPosErrorMessage(err));
      return;
    }
    setShowPayModal(true);
    window.setTimeout(() => {
      setIsPreparingPayment(false);
    }, 250);
  };

  const handleTakeawayPay = async ({ skipCustomerDetails = false } = {}) => {
    setCustomerError(null);
    setCustomerErrorField(null);
    if (isFinalOrderStatus(order?.status)) {
      setError("Order is no longer editable. Starting a new order...");
      resetToNewOrder("final_state");
      return;
    }
    const payload = {
      customerName: takeawayCustomerName.trim() || undefined,
      customerPhone: takeawayCustomerPhone.trim() || undefined,
      customerEmail: takeawayCustomerEmail.trim() || undefined,
    };

    const hasCustomerDetails =
      Boolean(payload.customerName) ||
      Boolean(payload.customerPhone) ||
      Boolean(payload.customerEmail);

    try {
      if (
        !skipCustomerDetails &&
        (requireCustomerDetails || showTakeawayCustomerModal)
      ) {
        const validation = validateCustomerDetails(
          {
            name: payload.customerName,
            phone: payload.customerPhone,
            email: payload.customerEmail,
          },
          { requireAll: true, requireEmail: false },
        );
        if (!validation.ok) {
          setCustomerError(validation.message);
          setCustomerErrorField(validation.field);
          if (validation.field) setActiveTakeawayCustomerField(validation.field);
          return;
        }
        await updateOrderCustomer({ orderId, payload }).unwrap();
      } else if (!skipCustomerDetails && hasCustomerDetails) {
        const validation = validateCustomerDetails(
          {
            name: payload.customerName,
            phone: payload.customerPhone,
            email: payload.customerEmail,
          },
          { requireAll: false },
        );
        if (!validation.ok) {
          setCustomerError(validation.message);
          setCustomerErrorField(validation.field);
          if (validation.field) setActiveTakeawayCustomerField(validation.field);
          return;
        }
        await updateOrderCustomer({ orderId, payload }).unwrap();
      }

      setIsPreparingPayment(true);
      setShowPostSubmitProcessing(true);
      setPaymentProgress({
        title: "Processing order...",
        message: "Please wait while we submit the order to the kitchen.",
        value: 20,
      });

      setShowTakeawayCustomerModal(false);
      setCustomerError(null);
      setCustomerErrorField(null);

      if (pendingCustomerAction === "SUBMIT") {
        setConfirmModal({
          isOpen: true,
          action: "SUBMIT_ORDER",
        });
      } else {
        await syncPendingDraftLines();
        setShowPayModal(true);
      }
      setShowPostSubmitProcessing(false);
      setIsPreparingPayment(false);
      setPendingCustomerAction(null);
      if (skipCustomerDetails) {
        setTakeawayCustomerName("");
        setTakeawayCustomerPhone("");
        setTakeawayCustomerEmail("");
        setActiveTakeawayCustomerField("phone");
        customerModalInitializedRef.current = false;
      }
    } catch (err) {
      setShowPostSubmitProcessing(false);
      setIsPreparingPayment(false);
      const title = err?.data?.title;
      if (isOrderingBlockedError(err)) {
        dispatch(api.util.invalidateTags(["StoreFeatures"]));
      }
      if (err?.status === 409 && title === "ITEM_NOT_ALLOWED_FOR_ORDER_TYPE") {
        setError(
          err?.data?.detail ||
            "This item is not available for this order type.",
        );
        refetchMenu();
        return;
      }
      if (await handleOrderLockedError(err)) return;
      setError(getPosErrorMessage(err));
    }
  };

  const handleProcessPayment = async (method, amount, tipAmount = 0) => {
    setActivePaymentMethod(method);
    const normalizedTipAmount = normalizeMoneyAmount(tipAmount);
    try {
      if (isPayFirstMode) {
        setIsRefreshingAfterPayment(true);
        setPaymentProgress({
          title: "Processing payment...",
          message: "Please wait while we send the payment request.",
          value: 25,
        });
        const invoices = await fetchOrderInvoices(orderId).unwrap();
        const list = Array.isArray(invoices) ? invoices : [];
        const invoiceId =
          resolvePayFirstInvoiceId({ invoices: list }) ||
          resolveReceiptInvoiceId({ invoices: list });

        if (!invoiceId) {
          setIsRefreshingAfterPayment(false);
          setError(
            "Unable to process payment because the invoice is not available yet. Please refresh and try again.",
          );
          return;
        }

        await processInvoicePayment({
          invoiceId,
          payload: {
            method,
            amount: normalizeMoneyAmount(amount),
            tipAmount: normalizedTipAmount,
          },
        }).unwrap();
        lastInvoicePaymentAttemptRef.current = null;
      } else {
        await processOrderPayment({
          orderId,
          payload: {
            method,
            amount: normalizeMoneyAmount(amount),
            tipAmount: normalizedTipAmount,
          },
        }).unwrap();
      }

      const refreshed = await refetch();
      const latestOrder = refreshed?.data || orderOverride || effectiveOrder || null;
      if (latestOrder) {
        setOrderOverride(latestOrder);
      }
      const remainingBalance = getRemainingBalance(
        latestOrder,
        grandTotalAmount,
        latestOrder ? 0 : totalPaid,
      );

      if (remainingBalance > 0) {
        setPaymentProgress({
          title: "Partial payment recorded",
          message: `Remaining balance: $${remainingBalance.toFixed(2)}. Please continue payment.`,
          value: 45,
        });
        setSuccessMessage(
          `Partial payment recorded. $${remainingBalance.toFixed(2)} remaining.`,
        );
        setIsRefreshingAfterPayment(false);
        return;
      }

      setIsRefreshingAfterPayment(true);

      // In PAY_FIRST mode, automatically submit to kitchen after payment
      if (isPayFirstMode) {
        setPaymentProgress({
          title: "Waiting for confirmation...",
          message: "Payment received. Waiting for the server to confirm it.",
          value: 55,
        });
        setSuccessMessage("Waiting for payment confirmation...");

        try {
          const invoices = await fetchOrderInvoices(orderId).unwrap();
          const list = Array.isArray(invoices) ? invoices : [];
          const invoiceId =
            resolvePayFirstInvoiceId({ invoices: list }) ||
            resolveReceiptInvoiceId({ invoices: list });

          if (!invoiceId) {
            setSuccessMessage(null);
            setError(
              "Invoice not found for this order. Payment cannot be confirmed yet. Please refresh and try again.",
            );
            setIsRefreshingAfterPayment(false);
            return;
          }

          const confirmedInvoice = await pollInvoiceUntilPaid({
            orderId,
            invoiceId,
            fetchOrderInvoices,
            onWaiting: () => {
              setSuccessMessage("Waiting for payment confirmation...");
              setPaymentProgress({
                title: "Waiting for confirmation...",
                message:
                  "Payment received. Waiting for the server to confirm it.",
                value: 70,
              });
            },
          });

          if (confirmedInvoice.state === "PAID_EMPTY") {
            setSuccessMessage(null);
            setError(
              "Payment captured but bill is empty; cannot submit/print. Please retry or contact support.",
            );
            setIsRefreshingAfterPayment(false);
            return;
          }

          if (confirmedInvoice.state === "TIMEOUT") {
            setSuccessMessage(null);
            setError("Payment pending; cannot submit/print.");
            setIsRefreshingAfterPayment(false);
            return;
          }

          if (confirmedInvoice.state !== "PAID_OK") {
            setSuccessMessage(null);
            setError("Unable to confirm payment; cannot submit/print.");
            setIsRefreshingAfterPayment(false);
            return;
          }

          setPaymentProgress({
            title: "Sending to kitchen...",
            message: "Payment is confirmed. Finishing the order now.",
            value: 88,
          });
          setSuccessMessage("Payment confirmed. Sending to kitchen...");

          const submitLatestPaidOrder = async () => {
            const refreshed = await refetch();
            const latestOrder =
              refreshed?.data || orderOverride || effectiveOrder || null;

            if (!latestOrder?.rowVersion) {
              throw new Error("Failed to refresh order data");
            }

            const result = await submitOrder({
              orderId,
              payload: {
                expectedRowVersion: latestOrder.rowVersion,
                submitRequestId: generateUUID(),
              },
            }).unwrap();

            return {
              result,
              latestOrder,
            };
          };

          let result = null;
          let latestOrderSnapshot = null;
          let staleRetryCount = 0;

          while (staleRetryCount < 3) {
            try {
              const submitOutcome = await submitLatestPaidOrder();
              result = submitOutcome.result;
              latestOrderSnapshot = submitOutcome.latestOrder;
              break;
            } catch (submitErr) {
              const errorCode =
                submitErr?.data?.code || submitErr?.data?.errorCode;
              if (errorCode !== POS_ERROR_CODES.CONFLICT_STALE_VERSION) {
                throw submitErr;
              }

              staleRetryCount += 1;
              setPaymentProgress({
                title: "Refreshing order...",
                message:
                  "Order changed on the server. Refreshing before finishing.",
                value: 90 + staleRetryCount * 3,
              });

              await sleep(300);
            }
          }

          if (!result) {
            const refreshed = await refetch();
            latestOrderSnapshot =
              refreshed?.data || orderOverride || effectiveOrder || null;

            const remainingDraftLines = buildDraftStateFromOrder(
              latestOrderSnapshot?.lines || [],
            );

            if (remainingDraftLines.length > 0) {
              throw new Error("Order is still updating. Please retry.");
            }

            result = {
              deltaLineCount: 0,
            };
          }

          clearLocalDraft(orderId);
          setLocalDraftLines([]);
          setOrderOverride(latestOrderSnapshot || null);
          const { deltaLineCount } = result;
          setSuccessMessage(
            `Submitted. Sent ${deltaLineCount} item${deltaLineCount !== 1 ? "s" : ""} to kitchen.`,
          );
          setPaymentProgress({
            title: "Done",
            message: "Payment completed. Returning to the table page.",
            value: 100,
          });

          setTimeout(() => {
            setShowPayModal(false);
            setError(null);
            setSuccessMessage(null);
            setIsRefreshingAfterPayment(false);
            resetToNewOrder("payment_complete");
          }, 1200);
        } catch (submitErr) {
          if (isOrderingBlockedError(submitErr)) {
            dispatch(api.util.invalidateTags(["StoreFeatures"]));
          }
          const latestOrder = (await refetch())?.data || null;
          const remainingDraftLines = buildDraftStateFromOrder(
            latestOrder?.lines || [],
          );
          if (
            latestOrder &&
            (isTerminalOrderStatus(latestOrder?.status) ||
              remainingDraftLines.length === 0)
          ) {
            clearLocalDraft(orderId);
            setLocalDraftLines([]);
            setOrderOverride(latestOrder);
            setPaymentProgress({
              title: "Done",
              message: "Payment completed. Returning to the table page.",
              value: 100,
            });
            setSuccessMessage("Payment completed. Returning to table page...");
            setTimeout(() => {
              setShowPayModal(false);
              setError(null);
              setSuccessMessage(null);
              setIsRefreshingAfterPayment(false);
              resetToNewOrder("payment_complete");
            }, 1200);
            return;
          }

          if (isItemStationRequiredError(submitErr)) {
            setSuccessMessage(null);
            setStationRequiredModalOpen(true);
            setIsRefreshingAfterPayment(false);
            return;
          }
          if (await handleOrderLockedError(submitErr)) {
            setSuccessMessage(null);
            setIsRefreshingAfterPayment(false);
            return;
          }
          if (isPaymentConfirmationRequiredError(submitErr)) {
            setSuccessMessage(null);
            setError(
              "Payment confirmation is required before submitting to kitchen. Please complete the payment confirmation and try again.",
            );
            setIsRefreshingAfterPayment(false);
            return;
          }
          setSuccessMessage(null);
          setError(getPosErrorMessage(submitErr));
          setIsRefreshingAfterPayment(false);
        }
      }
      // In PAY_LATER mode, return to seat board (order is complete)
      else {
        setSuccessMessage("Payment processed successfully.");
        setTimeout(() => {
          setSuccessMessage(null);
          setIsRefreshingAfterPayment(false);
          setShowPayModal(false);
          resetToNewOrder("payment_complete");
        }, 1200);
      }
    } catch (err) {
      if (isOrderingBlockedError(err)) {
        dispatch(api.util.invalidateTags(["StoreFeatures"]));
      }
      if (isPayFirstMode && lastInvoicePaymentAttemptRef.current) {
        lastInvoicePaymentAttemptRef.current = {
          ...lastInvoicePaymentAttemptRef.current,
          retryAllowed: isRetryablePaymentError(err),
        };
      }
      if (await handleOrderLockedError(err)) {
        setSuccessMessage(null);
        setIsRefreshingAfterPayment(false);
        return;
      }
      setSuccessMessage(null);
      setError(getPosErrorMessage(err));
      setIsRefreshingAfterPayment(false);
    } finally {
      setActivePaymentMethod(null);
    }
  };

  const handleChangeLineQty = async (line, delta) => {
    if (!line || !delta) return;
    if (isFinalOrderStatus(order?.status)) {
      setError("Order is no longer editable. Starting a new order...");
      resetToNewOrder("final_state");
      return;
    }
    setLocalDraftLines((currentLines) =>
      changeGroupedDraftLineQty({
        draftLines: currentLines,
        groupedLine: line,
        delta,
      }),
    );
  };

  const handleRemoveLine = async (line) => {
    if (!line) return;
    if (isFinalOrderStatus(order?.status)) {
      setError("Order is no longer editable. Starting a new order...");
      resetToNewOrder("final_state");
      return;
    }

    setLocalDraftLines((currentLines) =>
      removeGroupedDraftLine({
        draftLines: currentLines,
        groupedLine: line,
      }),
    );
  };

  const getNumeric = (value) => {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : 0;
  };
  const getLineDisplaySubtotal = React.useCallback(
    (line) => getNumeric(getLineSubtotalValue(line)),
    [],
  );

  const combinedOrderLines = React.useMemo(
    () =>
      buildCombinedDisplayLines({
        orderLines: effectiveOrderLines,
        draftLines: localDraftLines,
      }),
    [effectiveOrderLines, localDraftLines],
  );

  const groupedOrderLines = React.useMemo(
    () =>
      groupDisplayLines(
        combinedOrderLines,
        getNumeric,
        getLineDisplaySubtotal,
      )
        .map((line) => ({
          ...line,
          kitchenStatus: getKitchenStatus(line, getNumeric),
        }))
        .sort((a, b) => {
          const statusRank = {
            NOT_SENT: 0,
            PARTIALLY_SENT: 1,
            SENT: 2,
          };
          const rankDiff =
            (statusRank[a.kitchenStatus.code] ?? 99) -
            (statusRank[b.kitchenStatus.code] ?? 99);
          if (rankDiff !== 0) return rankDiff;
          return String(a.itemName || "").localeCompare(
            String(b.itemName || ""),
          );
        }),
    [combinedOrderLines, getLineDisplaySubtotal],
  );

  const pendingDisplayLines = React.useMemo(
    () =>
      groupedOrderLines
        .filter((line) => line.kitchenStatus.qtyPending > 0)
        .map((line) => ({
          ...line,
          displayQty: line.kitchenStatus.qtyPending,
          displaySubtotal: getSplitLineSubtotal(
            line,
            line.kitchenStatus.qtyPending,
            getNumeric,
          ),
          displayTotal: getSplitLineTotal(
            line,
            line.kitchenStatus.qtyPending,
            getNumeric,
          ),
        })),
    [groupedOrderLines],
  );

  const sentDisplayLines = React.useMemo(
    () =>
      groupedOrderLines
        .filter((line) => line.kitchenStatus.qtySent > 0)
        .map((line) => ({
          ...line,
          displayQty: line.kitchenStatus.qtySent,
          displaySubtotal: getSplitLineSubtotal(
            line,
            line.kitchenStatus.qtySent,
            getNumeric,
          ),
          displayTotal: getSplitLineTotal(
            line,
            line.kitchenStatus.qtySent,
            getNumeric,
          ),
        })),
    [groupedOrderLines],
  );

  const isDraft = order?.status === "DRAFT";
  const canEdit = isDraft;
  const canAddItems = !["COMPLETED", "CANCELLED"].includes(order?.status);

  const totals = order?.totals || {};
  const discounts = Array.isArray(order?.discounts) ? order.discounts : [];
  const serviceAmount = Number(
    totals.serviceCharge ?? totals.serviceFee ?? totals.serviceTotal ?? 0,
  );

  // Check if pending items need payment (for PAY_FIRST mode)
  const orderType = String(order?.orderType || "").toUpperCase();
  const isTakeawayOrder = orderType === "TAKEAWAY";
  const resolvedEntryVariant =
    entryVariant === "AUTO"
      ? isTakeawayOrder
        ? "TAKEAWAY"
        : "DINE_IN"
      : entryVariant;
  const isDineInNoTable = resolvedEntryVariant === "DINE_IN" && !isTakeawayOrder;
  const isTakeawayLike = isTakeawayOrder || isDineInNoTable;
  const isPayFirstMode = isTakeawayLike || posPaymentMode === "PAY_FIRST";
  const activeOrderId = routeOrderId || orderId || null;
  const pendingCustomer = readPendingCustomerFromStorage(activeOrderId);
  const customerName = String(
    order?.customerName || takeawayCustomerName || pendingCustomer?.name || "",
  ).trim();
  const customerPhone = String(
    order?.customerPhone ||
      takeawayCustomerPhone ||
      pendingCustomer?.phone ||
      "",
  ).trim();
  const hasHeaderCustomerDetails = Boolean(customerName || customerPhone);
  const showHeaderCustomerDetails =
    !isTakeawayOrder && posPaymentMode !== "PAY_FIRST";
  const totalPaid = getOrderPaidAmount(order);
  const sentItemsSubTotal = sentDisplayLines.reduce(
    (sum, line) => sum + Number(line.displaySubtotal || 0),
    0,
  );
  const sentItemsTaxTotal = sentDisplayLines.reduce((sum, line) => {
    const subtotal = Number(line.displaySubtotal || 0);
    const total = Number(line.displayTotal || 0);

    if (total >= subtotal && total > 0) {
      return sum + (total - subtotal);
    }

    return sum + (isLineNonTaxable(line) ? 0 : subtotal * activeTaxRate);
  }, 0);
  const pendingItemsSubTotal = pendingDisplayLines.reduce(
    (sum, line) => sum + (line.displaySubtotal || 0),
    0,
  );
  const pendingItemsTaxTotal = pendingDisplayLines.reduce(
    (sum, line) =>
      sum +
      (isLineNonTaxable(line)
        ? 0
        : (line.displaySubtotal || 0) * activeTaxRate),
    0,
  );
  const subTotalAmount = truncateMoneyAmount(
    sentItemsSubTotal + pendingItemsSubTotal,
  );
  const taxAmount = truncateMoneyAmount(
    sentItemsTaxTotal + pendingItemsTaxTotal,
  );
  const grandTotalAmount = truncateMoneyAmount(subTotalAmount + taxAmount);
  const hasApiFooterTotals =
    order?.status !== "DRAFT" &&
    order?.totals &&
    Number.isFinite(Number(order?.totals?.grandTotal));
  const footerSubTotalAmount = truncateMoneyAmount(
    hasApiFooterTotals ? order?.totals?.subTotal : subTotalAmount,
  );
  const footerTaxAmount = truncateMoneyAmount(
    hasApiFooterTotals ? order?.totals?.tax : taxAmount,
  );
  const footerGrandTotalAmount = truncateMoneyAmount(
    hasApiFooterTotals ? order?.totals?.grandTotal : grandTotalAmount,
  );
  const unpaidAmount = Math.max(0, footerGrandTotalAmount - totalPaid);
  const hasCartItems = groupedOrderLines.length > 0;
  const pendingItemsNeedPayment =
    isPayFirstMode &&
    pendingDisplayLines.length > 0 &&
    totalPaid < footerGrandTotalAmount;

  // In PAY_FIRST mode, can only submit after pending items are paid for
  const canSubmit =
    canAddItems &&
    groupedOrderLines.some((line) => line.kitchenStatus.qtyPending > 0) &&
    (!isPayFirstMode || !pendingItemsNeedPayment);

  const compactFooterButtons = canSubmit;
  const footerActionButtonClass = compactFooterButtons
    ? "h-6 w-full px-1 text-[8px] leading-none whitespace-nowrap"
    : "h-7 w-full px-2 text-[10px] leading-none whitespace-nowrap";
  // In PAY_FIRST mode, show PAY button even for DRAFT orders (except takeaway)
  const showPayButton =
    !isTakeawayLike &&
    (isPayFirstMode ? pendingItemsNeedPayment : !isDraft && unpaidAmount > 0);
  const showTakeawaySubmitButton = isTakeawayLike;
  const cartLeaveGuard = useCartLeaveGuard({
    when:
      pendingDisplayLines.length > 0 &&
      !isSubmitting &&
      !showPostSubmitProcessing &&
      !isRefreshingAfterPayment,
  });

  const footerButtonsCount =
    // (isDraft ? 0 : 1) + // split - HIDDEN
    // 1 + // discount - HIDDEN
    1 + // print bill
    (canSubmit && !isTakeawayLike ? 1 : 0) + // submit
    (showPayButton ? 1 : 0) + // pay
    (showTakeawaySubmitButton ? 1 : 0); // takeaway submit
  const pendingItemQtyTotal = pendingDisplayLines.reduce(
    (sum, line) => sum + Math.max(0, Number(line?.qty || 0)),
    0,
  );
  const submitButtonLabel = compactFooterButtons
    ? isSubmitting
      ? `SUBMIT... (${pendingItemQtyTotal})`
      : `SUBMIT (${pendingItemQtyTotal})`
    : isSubmitting
      ? `SUBMITTING... (${pendingItemQtyTotal})`
      : `SUBMIT TO KITCHEN (${pendingItemQtyTotal})`;

  // Helper text for PAY_FIRST mode
  const submitButtonHelper =
    isPayFirstMode && pendingItemsNeedPayment
      ? "Payment required before submitting to kitchen"
      : "";
  const shouldShowInitialOrderLoader =
    Boolean(routeOrderId) &&
    isLoading &&
    !activeFetchedOrder &&
    localDraftLines.length === 0;

  useKeyboardShortcuts(
    {
      [POS_SHORTCUTS.ESCAPE]: () => {
        if (showDiscount) setShowDiscount(false);
        else if (showNotesModal) setShowNotesModal(false);
        else if (showModifiers) setShowModifiers(false);
        else navigate("/pos");
      },
      [POS_SHORTCUTS.DISCOUNT]: () => {
        if (canEdit && !showDiscount) setShowDiscount(true);
      },
      [POS_SHORTCUTS.NOTES]: () => {
        if (canEdit && !showNotesModal) setShowNotesModal(true);
      },
      [POS_SHORTCUTS.SUBMIT]: () => {
        if (isPreparingPayment || isRefreshingAfterPayment) return;
        if (isTakeawayLike && hasCartItems) {
          handleTakeawaySubmit();
          return;
        }
        if (canSubmit) handleSubmit();
      },
      [POS_SHORTCUTS.PAYMENT]: () => {
        if (isPreparingPayment || isRefreshingAfterPayment) return;
        if (isTakeawayLike && hasCartItems) {
          handleTakeawaySubmit();
          return;
        }
        if (showPayButton && hasCartItems) handlePayment();
      },
    },
    !!order,
  );

  if (false && !orderId) {
    return (
      <div className="h-screen flex flex-col bg-slate-50 dark:bg-slate-950">
        {error && (
          <div className="fixed top-4 left-1/2 transform -translate-x-1/2 z-50 max-w-md">
            <Alert tone="error" onClose={() => setError(null)}>
              {error}
            </Alert>
          </div>
        )}

        <div className="flex-1 flex overflow-hidden">
          <div className="w-2/5 flex flex-col border-r border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900">
            <div className="p-4 space-y-2">
              <h2 className="text-lg font-semibold text-slate-900 dark:text-slate-100">
                Ready to start?
              </h2>
              <p className="text-sm text-slate-600 dark:text-slate-400">
                Select an item to create a new order.
              </p>
              {isCreatingOrder && (
                <div className="text-xs text-slate-500 dark:text-slate-400">
                  Creating order...
                </div>
              )}
            </div>
          </div>

          <div className="w-3/5 bg-slate-100 dark:bg-slate-800 flex overflow-hidden">
            <div className="flex-1 flex flex-col overflow-hidden">
              <div className="border-b border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 p-3">
                <div className="relative">
                  <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                    <svg
                      className="h-4 w-4 text-slate-400"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                      />
                    </svg>
                  </div>
                  <input
                    type="text"
                    placeholder="Search items..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="w-full h-10 rounded-lg border border-slate-300 dark:border-slate-600 bg-slate-50 dark:bg-slate-800 pl-9 pr-9 text-sm text-slate-900 dark:text-slate-100 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  />
                  {searchQuery && (
                    <button
                      type="button"
                      onClick={() => setSearchQuery("")}
                      className="absolute inset-y-0 right-0 flex items-center pr-3 text-slate-400 hover:text-slate-600 dark:hover:text-slate-300"
                    >
                      <svg
                        className="h-4 w-4"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M6 18L18 6M6 6l12 12"
                        />
                      </svg>
                    </button>
                  )}
                </div>
              </div>

              <div className="flex-1 overflow-y-auto p-3">
                <div className="grid grid-cols-3 gap-2">
                  {filteredItems.map((item, index) => {
                    const isSoldOut = item?.isAvailable === false;
                    const colorVariants = [
                      "bg-indigo-100 dark:bg-indigo-900/30 border-indigo-300 dark:border-indigo-700",
                      "bg-rose-100 dark:bg-rose-900/30 border-rose-300 dark:border-rose-700",
                      "bg-blue-100 dark:bg-blue-900/30 border-blue-300 dark:border-blue-700",
                      "bg-emerald-100 dark:bg-emerald-900/30 border-emerald-300 dark:border-emerald-700",
                      "bg-orange-100 dark:bg-orange-900/30 border-orange-300 dark:border-orange-700",
                      "bg-cyan-100 dark:bg-cyan-900/30 border-cyan-300 dark:border-cyan-700",
                    ];
                    const colorClass =
                      colorVariants[index % colorVariants.length];

                    return (
                      <button
                        key={item.id}
                        type="button"
                        onClick={() => handleItemSelect(item)}
                        disabled={isSoldOut || isAddingLine || isCreatingOrder}
                        className={`min-h-[84px] rounded-lg border ${
                          isSoldOut
                            ? "border-slate-300 bg-slate-100 text-slate-500 grayscale opacity-70 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-400"
                            : `${colorClass} hover:shadow-md hover:scale-[1.01] active:scale-[0.99]`
                        } p-2 flex flex-col items-center justify-center text-center transition-all disabled:cursor-not-allowed ${
                          !isSoldOut && (isAddingLine || isCreatingOrder)
                            ? "opacity-50"
                            : ""
                        }`}
                      >
                        {isSoldOut && (
                          <span className="mb-1 rounded-full bg-slate-200 px-2 py-0.5 text-[10px] font-black uppercase text-slate-600 dark:bg-slate-700 dark:text-slate-300">
                            Sold out
                          </span>
                        )}
                        <p className={`text-xs font-bold line-clamp-2 mb-0.5 ${
                          isSoldOut
                            ? "text-slate-500 dark:text-slate-400"
                            : "text-slate-900 dark:text-slate-100"
                        }`}>
                          {item.name}
                        </p>
                        <p className={`text-sm font-black ${
                          isSoldOut
                            ? "text-slate-500 dark:text-slate-400"
                            : "text-slate-900 dark:text-slate-100"
                        }`}>
                          ${item.basePrice.toFixed(2)}
                        </p>
                      </button>
                    );
                  })}
                </div>

                {filteredItems.length === 0 && (
                  <div className="flex items-center justify-center h-full">
                    <div className="text-center">
                      <svg
                        className="mx-auto h-10 w-10 text-slate-400"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4"
                        />
                      </svg>
                      <p className="mt-2 text-sm text-slate-600 dark:text-slate-400">
                        {searchQuery
                          ? "No items match your search"
                          : "No items available right now"}
                      </p>
                      {searchQuery && (
                        <button
                          type="button"
                          onClick={() => setSearchQuery("")}
                          className="mt-2 text-sm text-indigo-600 hover:text-indigo-700 font-medium"
                        >
                          Clear search
                        </button>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div className="w-44 border-l border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 flex flex-col">
              <div className="px-3 py-2 border-b border-slate-200 dark:border-slate-700">
                <h3 className="text-xs font-bold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                  Categories
                </h3>
              </div>
              <div className="flex-1 overflow-y-auto p-2 space-y-1.5">
                <button
                  type="button"
                  onClick={() => setCategoryFilter("")}
                  className={`w-full min-h-[48px] text-left px-4 py-3 rounded-lg text-sm font-bold uppercase transition ${
                    !categoryFilter
                      ? "bg-indigo-600 text-white shadow"
                      : "bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-700"
                  }`}
                >
                  All Items
                </button>
                {visibleParentCategories.map((category) => (
                  <button
                    key={category.id}
                    type="button"
                    onClick={() => setCategoryFilter(category.id)}
                    className={`w-full min-h-[48px] text-left px-4 py-3 rounded-lg text-sm font-semibold transition ${
                      categoryFilter === category.id
                        ? "bg-indigo-600 text-white shadow"
                        : "bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-700"
                    }`}
                  >
                    {category.name}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (shouldShowInitialOrderLoader) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 dark:bg-slate-950">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-4 border-indigo-600 border-t-transparent"></div>
          <p className="mt-4 text-slate-600 dark:text-slate-400">
            Loading order...
          </p>
        </div>
      </div>
    );
  }

  const handleExitToSeatBoard = async () => {
    if (!orderId) {
      navigate("/pos");
      return;
    }

    if (isDraft && totalPaid <= 0) {
      try {
        await closeOrder(orderId).unwrap();
      } catch (err) {
        setError(getPosErrorMessage(err));
        return;
      }
    }

    navigate("/pos");
  };

  const handleGoToAdmin = () => {
    if (!adminPath) return;
    navigate(adminPath);
  };

  return (
    <div className="flex h-full min-h-0 flex-col bg-slate-50 dark:bg-slate-950">
      {/* Real-time Update Notification */}
      {realtimeUpdate && (
        <div className="fixed top-4 right-4 z-50 animate-slide-in-right">
          <Alert tone="info">
            Order status updated: {realtimeUpdate.status}
          </Alert>
        </div>
      )}

      {/* Error Display */}
      {error && (
        <div className="fixed top-4 left-1/2 transform -translate-x-1/2 z-50 max-w-md">
          <Alert tone="error" onClose={() => setError(null)}>
            {error}
          </Alert>
        </div>
      )}

      {/* Success Display */}
      {successMessage && (
        <div className="fixed top-4 left-1/2 transform -translate-x-1/2 z-50 max-w-md">
          <Alert tone="success" onClose={() => setSuccessMessage(null)}>
            {successMessage}
          </Alert>
        </div>
      )}

      {showPostSubmitProcessing && (
        <div className="fixed inset-0 z-[80] bg-slate-900/30">
          <div className="flex h-full items-center justify-center px-4">
            <div className="w-full max-w-md rounded-2xl border border-slate-200 bg-white p-6 shadow-2xl dark:border-slate-700 dark:bg-slate-900">
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 animate-spin rounded-full border-4 border-slate-200 border-t-indigo-600 dark:border-slate-700 dark:border-t-indigo-400" />
                <div>
                  <p className="text-base font-semibold text-slate-900 dark:text-slate-100">
                    Processing order...
                  </p>
                  <p className="text-sm text-slate-600 dark:text-slate-400">
                    Please wait while we submit the order to the kitchen.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {showHeader && (
        <>
          {/* Header */}
          <div
            className={`border-b border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 ${
              isTakeawayOrder ? "px-4 py-3" : "px-3 py-2"
            }`}
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                {resolvedEntryVariant !== "DINE_IN" && !isTakeawayOrder && (
                  <button
                    type="button"
                    onClick={handleRequestClose}
                    className="p-1 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 transition"
                  >
                    <svg
                      className="h-4 w-4"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M15 19l-7-7 7-7"
                      />
                    </svg>
                  </button>
              )}
                <div className="flex items-center gap-2">
                  <h1 className="text-base font-bold text-slate-900 dark:text-slate-100">
                    {isTakeawayOrder
                      ? "Takeaway Order"
                      : order?.seatRef
                        ? order.seatRef
                        : "Dine-In Order"}
                  </h1>
                  {orderDuration && (
                    <div className="flex items-center gap-1 px-1.5 py-0.5 rounded-md bg-slate-100 dark:bg-slate-700">
                      <svg
                        className="h-3 w-3 text-slate-600 dark:text-slate-400"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                        />
                      </svg>
                      <span className="text-[10px] font-semibold text-slate-700 dark:text-slate-300">
                        {orderDuration}
                      </span>
                    </div>
                  )}
                </div>
              </div>

              <div className="flex items-start gap-2">
                {showHeaderCustomerDetails && (
                  <div className="min-w-0 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-right dark:border-slate-700 dark:bg-slate-800/60">
                    <p className="text-[10px] font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                      Customer
                    </p>
                    {hasHeaderCustomerDetails ? (
                      <div className="mt-1 space-y-0.5">
                        <p className="truncate text-xs font-semibold text-slate-900 dark:text-slate-100">
                          {customerName || "-"}
                        </p>
                        <p className="truncate text-xs text-slate-600 dark:text-slate-300">
                          {customerPhone || "-"}
                        </p>
                      </div>
                    ) : (
                      <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                        No customer added
                      </p>
                    )}
                  </div>
                )}
                <button
                  type="button"
                  onClick={toggleDarkMode}
                  className="rounded-lg p-2 text-slate-600 dark:text-slate-400 transition-all hover:bg-slate-200 dark:hover:bg-slate-700"
                  aria-label="Toggle dark mode"
                >
                  {isDarkMode ? (
                    <svg
                      className="h-5 w-5"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z"
                      />
                    </svg>
                  ) : (
                    <svg
                      className="h-5 w-5"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z"
                      />
                    </svg>
                  )}
                </button>
                <Button
                  size="sm"
                  variant="secondary"
                  className="inline-flex items-center whitespace-nowrap"
                  onClick={handleGoToAdmin}
                >
                  <svg
                    className="mr-1.5 h-4 w-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"
                    />
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                    />
                  </svg>
                  Admin
                </Button>
                {!cashSessionGuard.hasActiveCashSession && (
                  <Button
                    size="sm"
                    variant="secondary"
                    className="inline-flex items-center whitespace-nowrap"
                    onClick={cashSessionGuard.requestStartSession}
                  >
                    Start Session
                  </Button>
                )}
                {showCashManagementButtons && (
                  <>
                    <Button
                      size="sm"
                      variant="secondary"
                      className="inline-flex items-center whitespace-nowrap"
                      onClick={() => navigate("/pos/cash-up")}
                    >
                      Cash Up
                    </Button>
                  </>
                )}
                {!isTakeawayOrder && posEnableTakeaway && (
                  <Button
                    size="sm"
                    variant="secondary"
                    className="inline-flex items-center whitespace-nowrap"
                    onClick={() => navigate("/pos/takeaway")}
                  >
                    Takeaway
                  </Button>
                )}
                {resolvedEntryVariant !== "DINE_IN" && (
                  <Button
                    size="sm"
                    variant="secondary"
                    className="inline-flex items-center whitespace-nowrap"
                    onClick={() => navigate("/pos/dinein")}
                  >
                    Dine-In
                  </Button>
                )}
                {/*
                <Button
                  size="sm"
                  variant="secondary"
                  className="inline-flex items-center whitespace-nowrap"
                  onClick={() => setHeldOrdersModalOpen(true)}
                >
                  Held Orders{heldCount ? ` (${heldCount})` : ""}
                </Button>
                */}
              </div>
            </div>
          </div>
        </>
      )}

      <HeldOrdersModal
        isOpen={heldOrdersModalOpen}
        onClose={() => setHeldOrdersModalOpen(false)}
        onResume={(resumePath, heldOrder) =>
          navigate(resumePath, {
            state: {
              resumedHeldOrder: heldOrder,
            },
          })
        }
      />
      <Modal
        isOpen={Boolean(isOrderBlocked && !orderId)}
        onClose={() => {}}
        showCloseButton={false}
        size="md"
        align="center"
        backdropClassName="bg-black/20"
        zIndexClassName="z-40"
        panelClassName="border border-slate-200 dark:border-slate-700"
        bodyClassName="px-8 py-10 sm:px-10"
      >
        <div className="flex flex-col items-center text-center">
          <div className="mb-5 flex h-16 w-16 items-center justify-center rounded-full bg-red-50 text-red-600 dark:bg-red-950/40 dark:text-red-400">
            <svg
              aria-hidden="true"
              className="h-8 w-8"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={1.8}
                d="M5 10h14M6.5 10V7.5h11V10M7 10v8.5h10V10M9.5 14h5M4.5 21l15-18"
              />
            </svg>
          </div>
          <h3 className="text-xl font-semibold text-slate-900 dark:text-slate-100">
            Restaurant Closed
          </h3>
        
        </div>
      </Modal>
      {/* Main Content - Split Layout */}
      <div className="flex-1 flex overflow-hidden">
        {/* LEFT SIDE - Order Details (40%) */}
        <div className="w-2/5 border-r border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 flex flex-col">
          <div className="flex-1 flex flex-col overflow-hidden">
            <>
              <div className="flex-1 min-h-0 border-b border-slate-200 dark:border-slate-700">
                <div className="flex h-full flex-col p-4">
                  {/* PAY_FIRST mode alert */}
                  {isPayFirstMode && pendingItemsNeedPayment && (
                    <div className="mb-3 rounded-lg border-2 border-indigo-200 bg-indigo-50 dark:border-indigo-800 dark:bg-indigo-900/20 p-3">
                      <div className="flex items-start gap-2">
                        <svg
                          className="h-5 w-5 text-indigo-600 dark:text-indigo-400 flex-shrink-0 mt-0.5"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                          />
                        </svg>
                        <div>
                          <p className="text-xs font-bold text-indigo-900 dark:text-indigo-100">
                            Payment Required
                          </p>
                          <p className="mt-1 text-[11px] text-indigo-700 dark:text-indigo-300">
                            Pay ${unpaidAmount.toFixed(2)} before submitting
                            items to kitchen.
                          </p>
                        </div>
                      </div>
                    </div>
                  )}
                  <div className="mb-3 flex items-center justify-between">
                    <h2 className="text-sm font-semibold text-slate-700 dark:text-slate-300">
                      Items to Add
                    </h2>
                  </div>

                  {pendingDisplayLines.length > 0 ? (
                    <>
                      <div className="grid grid-cols-12 gap-1.5 px-2 pb-1 text-[10px] font-semibold text-orange-700 dark:text-orange-300">
                        <div className="col-span-4">Product</div>
                        <div className="col-span-2 text-right">Price</div>
                        <div className="col-span-2 pr-2 text-right">
                          Total
                        </div>
                        <div className="col-span-3 pl-2 text-center">Qty</div>
                        <div className="col-span-1 text-right"> </div>
                      </div>
                      <div className="flex-1 min-h-0 overflow-y-auto overflow-x-hidden pos-scrollbar pr-1">
                        <div className="space-y-1">
                        {pendingDisplayLines.map((line) => (
                          <div
                            key={`${line.orderLineId}-pending`}
                            className={`grid grid-cols-12 gap-1.5 items-center px-2 py-1 border-b border-orange-200 dark:border-orange-800 border-l-2 ${
                              line.kitchenStatus.code === "PARTIALLY_SENT"
                                ? "border-l-amber-500 bg-amber-50/30 dark:bg-amber-900/10"
                                : "border-l-orange-500 bg-orange-50/30 dark:bg-orange-900/10"
                            }`}
                          >
                            <div className="col-span-4">
                              <p className="text-[10px] font-medium leading-tight text-slate-900 dark:text-slate-100 truncate">
                                {line.itemName}
                              </p>
                              {isLineNonTaxable(line) && (
                                <p className="text-[9px] font-medium text-amber-700 dark:text-amber-300 truncate">
                                  Non Taxable
                                </p>
                              )}
                              {line.modifiers && line.modifiers.length > 0 && (
                                <p className="text-[9px] text-slate-500 dark:text-slate-400 truncate">
                                  {getModifierDisplayText(line.modifiers)}
                                </p>
                              )}
                            </div>
                            <div className="col-span-2 text-right text-[10px] text-slate-600 dark:text-slate-400">
                              ${getEffectiveUnitPrice(line).toFixed(2)}
                            </div>
                            <div className="col-span-2 pr-1 text-right text-[10px] font-bold text-slate-900 dark:text-slate-100">
                              ${Number(line.displaySubtotal || 0).toFixed(2)}
                            </div>
                            <div className="col-span-3 pl-1 flex items-center justify-center gap-1.5">
                              {canAddItems && (
                                <button
                                  type="button"
                                  className="h-7 w-8 rounded-md border border-slate-300 dark:border-slate-600 text-[11px] font-extrabold text-slate-700 dark:text-slate-200 bg-white dark:bg-slate-800 hover:bg-slate-100 dark:hover:bg-slate-700 active:scale-95 transition"
                                  onClick={() => handleChangeLineQty(line, -1)}
                                  aria-label="Decrease quantity"
                                >
                                  -
                                </button>
                              )}
                              <span className="min-w-[8px] text-center text-[10px] font-semibold text-slate-900 dark:text-slate-100">
                                {line.displayQty}
                              </span>
                              {canAddItems && (
                                <button
                                  type="button"
                                  className="h-7 w-8 rounded-md border border-slate-300 dark:border-slate-600 text-[11px] font-extrabold text-slate-700 dark:text-slate-200 bg-white dark:bg-slate-800 hover:bg-slate-100 dark:hover:bg-slate-700 active:scale-95 transition"
                                  onClick={() => handleChangeLineQty(line, 1)}
                                  aria-label="Increase quantity"
                                >
                                  +
                                </button>
                              )}
                            </div>
                            <div className="col-span-1 text-right">
                              {canAddItems && (
                                <button
                                  type="button"
                                  className="h-7 w-7 inline-flex items-center justify-center text-slate-400 hover:text-red-500 transition"
                                  onClick={() => handleRemoveLine(line)}
                                  aria-label="Remove item"
                                >
                                  <svg
                                    className="h-3.5 w-3.5"
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                  >
                                    <path
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                      strokeWidth={2}
                                      d="M6 18L18 6M6 6l12 12"
                                    />
                                  </svg>
                                </button>
                              )}
                            </div>
                          </div>
                        ))}
                        </div>
                      </div>
                    </>
                  ) : (
                    <div className="flex-1 min-h-0 flex items-center justify-center text-xs text-slate-500 dark:text-slate-400">
                      No pending items
                    </div>
                  )}
                </div>
              </div>

              {!isTakeawayLike && !isPayFirstMode && (
                <div className="flex-1 min-h-0">
                  <div className="flex h-full flex-col p-4">
                    <div className="mb-3 flex items-center justify-between">
                      <h2 className="text-sm font-semibold text-slate-700 dark:text-slate-300">
                        Already Sent for {order.seatRef}
                      </h2>
                      <span className="rounded-full bg-emerald-100 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300">
                        Sent
                      </span>
                    </div>

                    {sentDisplayLines.length > 0 ? (
                      <>
                        <div className="grid grid-cols-12 gap-1.5 px-2 pb-1 text-[10px] font-semibold text-emerald-700 dark:text-emerald-300">
                          <div className="col-span-5">Product</div>
                          <div className="col-span-2 text-right">Price</div>
                          <div className="col-span-2 pr-2 text-right">
                            Total
                          </div>
                          <div className="col-span-2 pl-2 text-center">
                            Qty
                          </div>
                          <div className="col-span-1 text-right"> </div>
                        </div>
                        <div className="flex-1 min-h-0 overflow-y-auto overflow-x-hidden pos-scrollbar pr-1">
                          <div className="space-y-1">
                          {sentDisplayLines.map((line) => (
                            <div
                              key={`${line.orderLineId}-sent`}
                              className="grid grid-cols-12 gap-1.5 items-center px-2 py-1 border-b border-emerald-200 dark:border-emerald-800 border-l-2 border-l-emerald-500 bg-emerald-50/30 dark:bg-emerald-900/10"
                            >
                              <div className="col-span-5">
                                <p className="text-[10px] font-medium leading-tight text-slate-900 dark:text-slate-100 truncate">
                                  {line.itemName}
                                </p>
                                {isLineNonTaxable(line) && (
                                  <p className="text-[9px] font-medium text-amber-700 dark:text-amber-300 truncate">
                                    Non Taxable
                                  </p>
                                )}
                                {line.modifiers &&
                                  line.modifiers.length > 0 && (
                                    <p className="text-[9px] text-slate-500 dark:text-slate-400 truncate">
                                      {getModifierDisplayText(line.modifiers)}
                                    </p>
                                  )}
                              </div>
                              <div className="col-span-2 text-right text-[10px] text-slate-600 dark:text-slate-400">
                                ${getEffectiveUnitPrice(line).toFixed(2)}
                              </div>
                              <div className="col-span-2 pr-1 text-right text-[10px] font-bold text-slate-900 dark:text-slate-100">
                                ${Number(line.displaySubtotal || 0).toFixed(2)}
                              </div>
                              <div className="col-span-2 pl-1 text-center text-[10px] font-semibold text-slate-900 dark:text-slate-100">
                                {line.displayQty}
                              </div>
                              <div className="col-span-1" />
                            </div>
                          ))}
                          </div>
                        </div>
                      </>
                    ) : (
                      <div className="flex-1 min-h-0 flex items-center justify-center text-xs text-slate-500 dark:text-slate-400">
                        No items on order
                      </div>
                    )}
                  </div>
                </div>
              )}
            </>
          </div>

          {/* Left Panel Bottom Bar - Actions and Total */}
          <div className="border-t border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 px-4 py-3">
            <div className="flex flex-col gap-2">
              <div className="flex items-center justify-between gap-3 text-[10px] text-slate-600 dark:text-slate-400">
                <div className="min-w-0 flex flex-nowrap items-center gap-x-3 overflow-x-auto">
                  <span className="whitespace-nowrap">
                    Subtotal:{" "}
                    <span className="font-medium text-slate-900 dark:text-slate-100">
                      ${footerSubTotalAmount.toFixed(2)}
                    </span>
                  </span>
                  {/* Discount value hidden for now. */}
                  <span className="text-xs whitespace-nowrap">
                    Tax:{" "}
                    <span className="text-xs font-semibold text-slate-900 dark:text-slate-100">
                      ${footerTaxAmount.toFixed(2)}
                    </span>
                  </span>
                  {serviceAmount > 0 && (
                    <span className="whitespace-nowrap">
                      Service:{" "}
                      <span className="font-medium text-slate-900 dark:text-slate-100">
                        ${serviceAmount.toFixed(2)}
                      </span>
                    </span>
                  )}
                </div>
                <span className="shrink-0 text-xs font-semibold text-slate-700 dark:text-slate-300 whitespace-nowrap">
                  Total:{" "}
                  <span className="text-sm font-bold text-indigo-600 dark:text-indigo-400">
                    ${footerGrandTotalAmount.toFixed(2)}
                  </span>
                </span>
              </div>

              <div
                className="grid w-full gap-1"
                style={{
                  gridTemplateColumns: `repeat(${footerButtonsCount}, minmax(0, 1fr))`,
                }}
              >
                {/* SPLIT BUTTON - TEMPORARILY HIDDEN */}
                {/* {!isDraft && (
                  <Button
                    size="sm"
                    variant="secondary"
                    onClick={handleSplitInvoice}
                    className={footerActionButtonClass}
                  >
                    SPLIT
                  </Button>
                )} */}
                {/* DISCOUNT BUTTON - TEMPORARILY HIDDEN */}
                {/* <Button
                  size="sm"
                  variant="secondary"
                  onClick={() => setShowDiscount(true)}
                  disabled={!canApplyDiscount}
                  className={footerActionButtonClass}
                >
                  DISCOUNT
                </Button> */}
                {/* <Button
                  size="sm"
                  variant="secondary"
                  onClick={() => setShowNotesModal(true)}
                  disabled={!canEdit}
                  className={footerActionButtonClass}
                >
                  NOTES
                </Button> */}
                <PrintBillAction
                  orderId={activeOrderId}
                  size="sm"
                  variant="secondary"
                  className={footerActionButtonClass}
                  disabled={isRefreshingAfterPayment}
                  onSuccess={setSuccessMessage}
                  onError={setError}
                />
                {isTakeawayLike ? (
                  <Button
                    size="sm"
                    onClick={handleTakeawaySubmit}
                    disabled={
                      !hasCartItems || isPreparingPayment || isRefreshingAfterPayment
                    }
                    className={`${footerActionButtonClass} bg-indigo-600 text-white hover:bg-indigo-700 border-indigo-600`}
                  >
                    {isPreparingPayment || isRefreshingAfterPayment
                      ? "SUBMITTING..."
                      : "SUBMIT"}
                  </Button>
                ) : (
                  <>
                    {/* In PAY_FIRST mode with pending items needing payment, show PAY button */}
                    {isPayFirstMode && pendingItemsNeedPayment && (
                      <Button
                        size="sm"
                        onClick={handlePayment}
                        disabled={!hasCartItems}
                        className={`${footerActionButtonClass} bg-indigo-600 text-white hover:bg-indigo-700 border-indigo-600`}
                        title={`Pay $${unpaidAmount.toFixed(2)} for pending items`}
                      >
                        PAY
                      </Button>
                    )}
                    {/* Show SUBMIT button when items can be submitted */}
                    {canSubmit && (
                      <Button
                        size="sm"
                        onClick={handleSubmit}
                        disabled={isSubmitting}
                        className={footerActionButtonClass}
                        title={submitButtonHelper}
                      >
                        {submitButtonLabel}
                      </Button>
                    )}
                    {/* Show PAY button in PAY_LATER mode or after items are submitted */}
                    {showPayButton &&
                      !(isPayFirstMode && pendingItemsNeedPayment) &&
                      !(isPayFirstMode && pendingDisplayLines.length > 0) && (
                        <Button
                          size="sm"
                          onClick={handlePayment}
                          disabled={!hasCartItems}
                          className={footerActionButtonClass}
                        >
                          PAY
                        </Button>
                      )}
                  </>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* RIGHT SIDE - Kiosk Menu (60%) */}
        <div className="w-3/5 bg-slate-100 dark:bg-slate-800 flex overflow-hidden">
          {/* Items Area */}
          <div className="flex-1 flex flex-col overflow-hidden">
            <div className="border-b border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 p-3">
              <div className="relative">
                <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                  <svg
                    className="h-4 w-4 text-slate-400"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                    />
                  </svg>
                </div>
                <input
                  type="text"
                  inputMode="search"
                  enterKeyHint="search"
                  placeholder="Search items..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full h-10 rounded-lg border border-slate-300 dark:border-slate-600 bg-slate-50 dark:bg-slate-800 pl-9 pr-9 text-sm text-slate-900 dark:text-slate-100 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                />
                {searchQuery && (
                  <button
                    type="button"
                    onClick={() => setSearchQuery("")}
                    className="absolute inset-y-0 right-0 flex items-center pr-3 text-slate-400 hover:text-slate-600 dark:hover:text-slate-300"
                  >
                    <svg
                      className="h-4 w-4"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M6 18L18 6M6 6l12 12"
                      />
                    </svg>
                  </button>
                )}
              </div>
            </div>

            <div className="flex-1 overflow-y-auto p-2.5">
              {showParentCategoryBrowser ? (
                <div className="space-y-4">
                  {showChildCategorySection && (
                    <div className="space-y-2">
                      {useCategorySectionHeading && (
                        <h4 className="px-1 text-xs font-bold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                          Categories
                        </h4>
                      )}
                      <div className="grid grid-cols-4 gap-2">
                        {visibleChildCategories.map((category, index) => {
                          const colorVariants = [
                            "bg-amber-100 dark:bg-amber-900/30 border-amber-300 dark:border-amber-700",
                            "bg-rose-100 dark:bg-rose-900/30 border-rose-300 dark:border-rose-700",
                            "bg-blue-100 dark:bg-blue-900/30 border-blue-300 dark:border-blue-700",
                            "bg-emerald-100 dark:bg-emerald-900/30 border-emerald-300 dark:border-emerald-700",
                            "bg-orange-100 dark:bg-orange-900/30 border-orange-300 dark:border-orange-700",
                            "bg-cyan-100 dark:bg-cyan-900/30 border-cyan-300 dark:border-cyan-700",
                          ];
                          const colorClass =
                            colorVariants[index % colorVariants.length];

                          return (
                            <button
                              key={category.id}
                              type="button"
                              onClick={() => setCategoryFilter(category.id)}
                              className={`min-h-[84px] rounded-lg border ${colorClass} p-2 flex flex-col items-center justify-center text-center hover:shadow-md hover:scale-[1.01] active:scale-[0.99] transition-all`}
                            >
                              <p className="text-xs font-medium uppercase tracking-wide text-slate-700 dark:text-slate-300 mb-1">
                                Category
                              </p>
                              <p className="text-sm font-black text-slate-900 dark:text-slate-100 line-clamp-2">
                                {category.name}
                              </p>
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  )}
                  {showParentItemSection && (
                    <div className="space-y-2">
                      {useItemSectionHeading && (
                        <h4 className="px-1 text-xs font-bold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                          Items
                        </h4>
                      )}
                      <div className="grid grid-cols-4 gap-2">
                        {selectedParentItems.map((item, index) => {
                          const colorVariants = [
                            "bg-amber-100 dark:bg-amber-900/30 border-amber-300 dark:border-amber-700",
                            "bg-rose-100 dark:bg-rose-900/30 border-rose-300 dark:border-rose-700",
                            "bg-blue-100 dark:bg-blue-900/30 border-blue-300 dark:border-blue-700",
                            "bg-emerald-100 dark:bg-emerald-900/30 border-emerald-300 dark:border-emerald-700",
                            "bg-orange-100 dark:bg-orange-900/30 border-orange-300 dark:border-orange-700",
                            "bg-cyan-100 dark:bg-cyan-900/30 border-cyan-300 dark:border-cyan-700",
                          ];
                          const colorClass =
                            colorVariants[index % colorVariants.length];

                          return (
                            <button
                              key={item.id}
                              type="button"
                              onClick={() => handleItemSelect(item)}
                              disabled={isAddingLine || isCreatingOrder}
                              className={`min-h-[84px] rounded-lg border ${colorClass} p-2 flex flex-col items-center justify-center text-center hover:shadow-md hover:scale-[1.01] active:scale-[0.99] transition-all disabled:opacity-50 disabled:cursor-not-allowed`}
                            >
                              <p className="text-xs font-bold text-slate-900 dark:text-slate-100 line-clamp-2 mb-0.5">
                                {item.name}
                              </p>
                              <p className="text-sm font-black text-slate-900 dark:text-slate-100">
                                ${item.basePrice.toFixed(2)}
                              </p>
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <div className="space-y-3">
                  {!searchQuery && selectedCategory?.parentCategoryId && (
                    <div className="flex justify-end">
                      <button
                        type="button"
                        onClick={() =>
                          setCategoryFilter(String(selectedCategory.parentCategoryId))
                        }
                        className="inline-flex items-center rounded-lg bg-slate-200 px-3 py-2 text-sm font-medium text-slate-700 transition hover:bg-slate-300 dark:bg-slate-700 dark:text-slate-100 dark:hover:bg-slate-600"
                      >
                        Back
                      </button>
                    </div>
                  )}
                  <div className="grid grid-cols-4 gap-2">
                    {filteredItems.map((item, index) => {
                      const isSoldOut = item?.isAvailable === false;
                      const colorVariants = [
                        "bg-amber-100 dark:bg-amber-900/30 border-amber-300 dark:border-amber-700",
                        "bg-rose-100 dark:bg-rose-900/30 border-rose-300 dark:border-rose-700",
                        "bg-blue-100 dark:bg-blue-900/30 border-blue-300 dark:border-blue-700",
                        "bg-emerald-100 dark:bg-emerald-900/30 border-emerald-300 dark:border-emerald-700",
                        "bg-orange-100 dark:bg-orange-900/30 border-orange-300 dark:border-orange-700",
                        "bg-cyan-100 dark:bg-cyan-900/30 border-cyan-300 dark:border-cyan-700",
                      ];
                      const colorClass =
                        colorVariants[index % colorVariants.length];

                      return (
                        <button
                          key={item.id}
                          type="button"
                          onClick={() => handleItemSelect(item)}
                          disabled={isSoldOut || isAddingLine || isCreatingOrder}
                          className={`min-h-[84px] rounded-lg border ${
                            isSoldOut
                              ? "border-slate-300 bg-slate-100 text-slate-500 grayscale opacity-70 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-400"
                              : `${colorClass} hover:shadow-md hover:scale-[1.01] active:scale-[0.99]`
                          } p-2 flex flex-col items-center justify-center text-center transition-all disabled:cursor-not-allowed ${
                            !isSoldOut && (isAddingLine || isCreatingOrder)
                              ? "opacity-50"
                              : ""
                          }`}
                        >
                          {isSoldOut && (
                            <span className="mb-1 rounded-full bg-slate-200 px-2 py-0.5 text-[10px] font-black uppercase text-slate-600 dark:bg-slate-700 dark:text-slate-300">
                              Sold out
                            </span>
                          )}
                          <p className={`text-xs font-bold line-clamp-2 mb-0.5 ${
                            isSoldOut
                              ? "text-slate-500 dark:text-slate-400"
                              : "text-slate-900 dark:text-slate-100"
                          }`}>
                            {item.name}
                          </p>
                          <p className={`text-sm font-black ${
                            isSoldOut
                              ? "text-slate-500 dark:text-slate-400"
                              : "text-slate-900 dark:text-slate-100"
                          }`}>
                            ${item.basePrice.toFixed(2)}
                          </p>
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

              {!showParentCategoryBrowser &&
                isMenuLoading &&
                filteredItems.length === 0 && (
                  <div className="flex items-center justify-center h-full">
                    <div className="text-center">
                      <div className="mx-auto flex h-10 w-10 items-center justify-center rounded-full border-2 border-slate-300 border-t-indigo-500 animate-spin" />
                      <p className="mt-3 text-sm font-medium text-slate-600 dark:text-slate-400">
                        Loading items...
                      </p>
                    </div>
                  </div>
                )}

              {!showParentCategoryBrowser &&
                !isMenuLoading &&
                filteredItems.length === 0 && (
                <div className="flex items-center justify-center h-full">
                  <div className="text-center">
                    <svg
                      className="mx-auto h-10 w-10 text-slate-400"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4"
                      />
                    </svg>
                    <p className="mt-2 text-sm text-slate-600 dark:text-slate-400">
                      {searchQuery
                        ? "No items match your search"
                        : "No items available right now"}
                    </p>
                    {!searchQuery && selectedCategory?.parentCategoryId && (
                      <button
                        type="button"
                        onClick={() =>
                          setCategoryFilter(String(selectedCategory.parentCategoryId))
                        }
                        className="mt-3 inline-flex items-center rounded-lg bg-slate-200 px-3 py-2 text-sm font-medium text-slate-700 transition hover:bg-slate-300 dark:bg-slate-700 dark:text-slate-100 dark:hover:bg-slate-600"
                      >
                        Back
                      </button>
                    )}
                    {searchQuery && (
                      <button
                        type="button"
                        onClick={() => setSearchQuery("")}
                        className="mt-2 text-sm text-indigo-600 hover:text-indigo-700 font-medium"
                      >
                        Clear search
                      </button>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Category Rail */}
          <div className="w-44 border-l border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 flex flex-col">
            <div className="px-3 py-2 border-b border-slate-200 dark:border-slate-700">
              <h3 className="text-xs font-bold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                Categories
              </h3>
            </div>
            <div className="flex-1 overflow-y-auto p-2 space-y-1.5">
              <button
                type="button"
                onClick={() => setCategoryFilter("")}
                className={`w-full min-h-[48px] text-left px-4 py-3 rounded-lg text-sm font-bold uppercase transition ${
                  !categoryFilter
                    ? "bg-indigo-600 text-white shadow"
                    : "bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-700"
                }`}
              >
                All Items
              </button>
              {visibleParentCategories.map((category) => (
                <button
                  key={category.id}
                  type="button"
                  onClick={() => setCategoryFilter(category.id)}
                  className={`w-full min-h-[48px] text-left px-4 py-3 rounded-lg text-sm font-semibold transition ${
                    activeRailCategoryId === category.id
                      ? "bg-indigo-600 text-white shadow"
                      : "bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-700"
                  }`}
                >
                  {category.name}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Modifier Selector */}
      {selectedItem && (
        <ModifierSelector
          isOpen={showModifiers}
          onClose={() => {
            setShowModifiers(false);
            setSelectedItem(null);
            setModifierPreset([]);
            setSkipUpsellForSelectedItem(false);
          }}
          onConfirm={handleAddItem}
          modifierGroups={selectedItemModifierGroups}
          selectedModifiers={modifierPreset}
          itemName={selectedItem.name}
          itemPrice={selectedItem.basePrice}
        />
      )}

      {/* Discount Form */}
      <DiscountForm
        isOpen={showDiscount}
        onClose={() => setShowDiscount(false)}
        orderId={orderId}
        orderSubtotal={order.totals.subTotal}
        discounts={discounts}
      />

      {/* Notes Modal */}
      <Modal
        isOpen={showNotesModal}
        onClose={() => setShowNotesModal(false)}
        title="Order Notes"
        confirmText="Save"
        onConfirm={handleUpdateNotes}
        confirmDisabled={isUpdatingNotes}
      >
        <Textarea
          label="Notes"
          placeholder="Add special instructions or notes..."
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          rows={4}
        />
      </Modal>


      {/* Takeaway Customer Details */}
      <Modal
        isOpen={showTakeawayCustomerModal}
        onClose={handleCloseTakeawayCustomerModal}
        title={null}
        size="xl"
      >
        <div className="space-y-4">
          <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
            <div>
              <Input
                ref={takeawayCustomerPhoneInputRef}
                label="Phone"
                required
                type="tel"
                enterKeyHint="next"
                className={
                  customerErrorField === "phone"
                    ? "ring-2 ring-rose-500 dark:ring-rose-400"
                    : activeTakeawayCustomerField === "phone"
                      ? "ring-2 ring-indigo-500 dark:ring-indigo-400"
                      : ""
                }
                value={takeawayCustomerPhone}
                onChange={(e) =>
                  handleTakeawayCustomerPhoneChange(e.target.value)
                }
                onFocus={() => setActiveTakeawayCustomerField("phone")}
                placeholder="Enter 10 digits Phone Number"
                error={customerErrorField === "phone" ? customerError : null}
              />
            </div>
            <Input
              ref={takeawayCustomerNameInputRef}
              label="Name"
              required
              enterKeyHint="next"
              className={
                activeTakeawayCustomerField === "name"
                  ? "ring-2 ring-indigo-500 dark:ring-indigo-400"
                  : ""
              }
              value={takeawayCustomerName}
              disabled={!isTakeawayCustomerPhoneValid}
              onChange={(e) => {
                setTakeawayCustomerName(e.target.value);
                if (customerErrorField === "name") {
                  setCustomerError(null);
                  setCustomerErrorField(null);
                }
              }}
              onMouseDown={(e) => {
                if (activeTakeawayCustomerField !== "phone") return;
                if (ensureValidTakeawayPhoneBeforeAdvance()) return;
                e.preventDefault();
                e.stopPropagation();
              }}
              onPointerDown={(e) => {
                if (activeTakeawayCustomerField !== "phone") return;
                if (ensureValidTakeawayPhoneBeforeAdvance()) return;
                e.preventDefault();
                e.stopPropagation();
              }}
              onFocus={() => {
                if (
                  activeTakeawayCustomerField === "phone" &&
                  !ensureValidTakeawayPhoneBeforeAdvance()
                ) {
                  return;
                }
                setActiveTakeawayCustomerField("name");
              }}
              placeholder="John Doe"
              error={customerErrorField === "name" ? customerError : null}
            />
            <Input
              ref={takeawayCustomerEmailInputRef}
              label="Email (Optional)"
              type="email"
              enterKeyHint="done"
              className={
                activeTakeawayCustomerField === "email"
                  ? "ring-2 ring-indigo-500 dark:ring-indigo-400"
                  : ""
              }
              value={takeawayCustomerEmail}
              disabled={!isTakeawayCustomerPhoneValid}
              onChange={(e) => {
                setTakeawayCustomerEmail(e.target.value);
                if (customerErrorField === "email") {
                  setCustomerError(null);
                  setCustomerErrorField(null);
                }
              }}
              onMouseDown={(e) => {
                if (activeTakeawayCustomerField !== "phone") return;
                if (ensureValidTakeawayPhoneBeforeAdvance()) return;
                e.preventDefault();
                e.stopPropagation();
              }}
              onPointerDown={(e) => {
                if (activeTakeawayCustomerField !== "phone") return;
                if (ensureValidTakeawayPhoneBeforeAdvance()) return;
                e.preventDefault();
                e.stopPropagation();
              }}
              onFocus={() => {
                if (
                  activeTakeawayCustomerField === "phone" &&
                  !ensureValidTakeawayPhoneBeforeAdvance()
                ) {
                  return;
                }
                setActiveTakeawayCustomerField("email");
              }}
              placeholder="john@demo.com"
              error={customerErrorField === "email" ? customerError : null}
            />
          </div>
          {customerError && !customerErrorField && (
            <div className="text-xs text-rose-600 dark:text-rose-400">
              {customerError}
            </div>
          )}

          <OnScreenKeyboard
            layout={activeTakeawayCustomerField === "phone" ? "numeric" : "alpha"}
            size={activeTakeawayCustomerField === "phone" ? "lg" : "md"}
            value={getActiveTakeawayCustomerValue()}
            onChange={setActiveTakeawayCustomerValue}
            inputRef={
              activeTakeawayCustomerField === "phone"
                ? takeawayCustomerPhoneInputRef
                : activeTakeawayCustomerField === "name"
                  ? takeawayCustomerNameInputRef
                  : takeawayCustomerEmailInputRef
            }
            onDone={handleTakeawayCustomerKeyboardDone}
            showDone={false}
            disabled={isUpdatingCustomer}
          />

          <div className="flex items-center justify-end gap-2">
            <Button
              size="sm"
              variant="secondary"
              onClick={handleCloseTakeawayCustomerModal}
            >
              Back
            </Button>
            <Button
              size="sm"
              variant="ghost"
              onClick={() =>
                handleTakeawayPay({ skipCustomerDetails: true })
              }
              disabled={isUpdatingCustomer || isPreparingPayment}
            >
              Skip
            </Button>
            <Button size="sm" onClick={() => handleTakeawayPay()}>
              {isUpdatingCustomer ? "Saving..." : "Continue"}
            </Button>
          </div>
        </div>
      </Modal>

      {/* Pay Modal */}
      <ProcessPaymentModal
        isOpen={showPayModal}
        onClose={() => setShowPayModal(false)}
        subtotal={footerSubTotalAmount}
        tax={footerTaxAmount}
        total={footerGrandTotalAmount}
        amountDue={Math.max(unpaidAmount, 0)}
        paymentError={paymentError}
        tipConfig={tipConfig}
        isProcessing={isProcessingPayment || isRefreshingAfterPayment || isPreparingPayment}
        activePaymentMethod={activePaymentMethod}
        onCash={(amount, tipAmount) =>
          handleProcessPayment("CASH", amount, tipAmount)
        }
        onCard={(amount, tipAmount) =>
          handleProcessPayment("CARD", amount, tipAmount)
        }
      />

      <CashSessionStartupModal
        autoPrompt={false}
        isOpenRequested={cashSessionGuard.isStartSessionRequested}
        onSessionStarted={cashSessionGuard.clearStartSessionRequest}
      />

      {/* Confirm Modal */}
      <Modal
        isOpen={confirmModal.isOpen}
        onClose={() => setConfirmModal({ isOpen: false, action: null })}
        title={
          confirmModal.action === "CLOSE_ORDER"
            ? "Close Order"
            : "Submit To Kitchen"
        }
        message={
          confirmModal.action === "CLOSE_ORDER"
            ? "You have unsent draft items. Closing will discard them. Are you sure you want to close this order?"
            : "Submit this order to the kitchen?"
        }
        confirmText={confirmModal.action === "CLOSE_ORDER" ? "Close" : "Submit"}
        confirmVariant="pos-primary"
        onConfirm={handleConfirmAction}
        isLoading={isSubmitting}
      />

      <Modal
        isOpen={stationRequiredModalOpen}
        onClose={() => setStationRequiredModalOpen(false)}
        title="Station Assignment Required"
        message={ITEM_STATION_REQUIRED_MESSAGE}
        onConfirm={() => setStationRequiredModalOpen(false)}
        confirmText="OK"
        showCancel={false}
      />
      <Modal
        isOpen={cartLeaveGuard.isOpen}
        onClose={cartLeaveGuard.cancelLeave}
        title="Leave Page?"
        message="You have unsent cart items. Leaving this page will remove them. Are you sure you want to continue?"
        confirmText="Leave Page"
        cancelText="Keep Editing"
        confirmVariant="danger"
        onConfirm={cartLeaveGuard.confirmLeave}
      />

      {/* Upsell Modal */}
      <UpsellSuggestionModal
        isOpen={upsellModal.isOpen}
        onClose={() =>
          setUpsellModal({
            isOpen: false,
            triggerItemName: "",
            triggerItemPrice: null,
            suggestions: [],
          })
        }
        triggerItemName={upsellModal.triggerItemName}
        triggerItemPrice={upsellModal.triggerItemPrice}
        suggestions={upsellModal.suggestions}
        onConfirm={handleConfirmUpsellItems}
        isAdding={isAddingLine}
      />

      {(isPreparingPayment || isRefreshingAfterPayment) && (
        <div className="fixed inset-0 z-50 bg-slate-900/20">
          <div className="flex h-full items-center justify-center px-4">
            <div className="w-full max-w-md rounded-2xl border border-slate-200 bg-white px-6 py-5 shadow-xl dark:border-slate-700 dark:bg-slate-900">
              <div className="flex items-center gap-3">
                <div className="h-12 w-12 animate-spin rounded-full border-4 border-slate-200 border-t-indigo-600 dark:border-slate-700 dark:border-t-indigo-400" />
                <div>
                  <p className="text-sm font-semibold text-slate-900 dark:text-slate-100">
                    {paymentProgress.title}
                  </p>
                  <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                    {paymentProgress.message}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default TakeawayOrderEditor;
